import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Card from "@/components/ui/Card.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import {
  CheckoutPaymentMethodRow,
  PaymentDetailsHeader,
  TestPaymentHelper,
} from "@/components/payments/CheckoutPaymentDetails.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient.js";
import { applyDefaultAppointmentReminderPreference } from "@/utils/notificationPreferences.js";
import { patchOwnNotificationPreferences } from "@/services/notificationPreferencesService.js";
import { loadDoctorPaymentMethods } from "@/services/doctorPaymentPreferences.js";
import {
  filterAppointmentPaystackMethodsForPatient,
  recordAppointmentPaymentCapabilityDiagnostics,
} from "@/services/appointmentPaystackCapabilities.js";
import { loadSupportedAppointmentPaymentMethods } from "@/services/appointmentPaymentCapabilities.js";
import {
  createAppointmentPaymentIntent,
  isCardPaymentMethod,
} from "@/services/appointmentCheckoutService.js";
import {
  initializePaystackAppointmentPayment,
  initializeMtnMomoAppointmentPayment,
  recoverMtnMomoAppointmentPayment,
  initializePaystackPaymentHarness,
  syncAppointmentPayment,
  syncSponsorPaymentSession,
  verifyPaystackAppointmentPayment,
  verifyMtnMomoAppointmentPayment,
  verifyPaystackPaymentHarness,
} from "@/lib/payments/api.ts";
import {
  clearPendingSponsorPayment,
  readPendingSponsorPayment,
} from "@/lib/payments/sponsorPaymentRecovery.js";
import { openPaystackInlineCheckout } from "@/lib/payments/paystackInline.js";
import { activePaymentProviderMode, isPaystackRuntime, isPaystackTestHelperRuntime, isPaystackTestRuntime, isWorldlineRuntime } from "@/lib/payments/providerRuntime.js";
import {
  createAppointment,
  updateAppointmentPayment,
} from "@/services/appointmentService.js";
import { normalizeCareSubjectFromBooking } from "@/services/dependantCareService.js";
import { normalizeDoctorPaymentMethodId } from "@/services/doctorPaymentPreferences.js";
import {
  createAppointmentNotification,
  resolveDoctorAuthUserId,
} from "@/services/ehrAccessService.js";
import { sendAppointmentEventEmail } from "@/services/appointmentEmailService.js";
import { loadDoctorTermsDisclosure } from "@/services/doctorBillingBrandingService.js";
import { resolvePatientDisplayName } from "@/utils/patientDisplayName.js";
import { usePatientFacingClinicianName } from "@/hooks/usePatientFacingClinicianName.js";
import { writeUserActivityEvent } from "@/services/userTruthTracking.js";
import {
  getEvaluationAccessEligibility,
  redeemEvaluationAccessBooking,
} from "@/services/evaluationAccessService.js";
import {
  createWorldlineHostedCheckout,
  createWorldlineSponsorLink,
  getWorldlineSandboxAvailability,
  isWorldlineSandboxEntryEnabled,
  revokeWorldlineSponsorLink,
} from "@/services/worldlineHostedCheckoutService.js";

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

async function persistPatientAppointmentReminderDefault(userId = "") {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return { defaulted: false };

  const { data: profile, error: profileError } = await supabase
    .from("patient_profiles")
    .select("notification_prefs, updated_at")
    .eq("user_id", normalizedUserId)
    .maybeSingle();
  if (profileError || !profile) return { defaulted: false };

  const next = applyDefaultAppointmentReminderPreference(profile.notification_prefs, "patient");
  if (!next.changed) return { defaulted: false };

  const { data, error: persistError } = await patchOwnNotificationPreferences({
    role: "patient",
    expectedUpdatedAt: profile.updated_at || null,
    patch: { appointment_reminders: next.appointmentReminderPatch },
    source: "appointment_booking_default",
    onlyIfAppointmentReminderMissing: true,
  });
  if (persistError) return { defaulted: false };
  return {
    defaulted: Boolean(data?.notification_prefs?.appointment_reminders?.enabled),
    notificationPrefs: data?.notification_prefs || null,
    updatedAt: data?.updated_at || null,
  };
}

function categorizeCheckoutError(error) {
  const message = String(error?.body?.code || error?.code || error?.message || error || "")
    .trim()
    .toLowerCase();
  if (!message) return "unknown";
  if (message.includes("auth") || message.includes("session") || message.includes("login")) {
    return "auth";
  }
  if (message.includes("network") || message.includes("fetch") || message.includes("timeout")) {
    return "network";
  }
  if (message.includes("stripe") || message.includes("payment_intent") || message.includes("card")) {
    return "payment_provider";
  }
  if (message.includes("payout") || message.includes("connect")) {
    return "clinician_payout";
  }
  if (message.includes("missing") || message.includes("invalid") || message.includes("incomplete")) {
    return "validation";
  }
  if (message.includes("rate")) return "rate_limited";
  return "unknown";
}

function hasExplicitCareSubjectState(state) {
  if (!state || typeof state !== "object") return false;
  return Boolean(
    state?.careSubject ||
      state?.careSubjectType ||
      state?.careSubjectId ||
      state?.careSubjectName ||
      state?.careSubjectRelationship ||
      state?.care_subject_type ||
      state?.care_subject_dependant_id ||
      state?.care_subject_relationship,
  );
}

function mergeCareSubjectFromStored(locationState, storedState) {
  if (!locationState || typeof locationState !== "object") return storedState || null;
  if (!storedState || typeof storedState !== "object") return locationState;
  if (hasExplicitCareSubjectState(locationState) && !hasExplicitCareSubjectState(storedState)) {
    return locationState;
  }

  return {
    ...storedState,
    ...locationState,
    careSubject: storedState?.careSubject ?? locationState?.careSubject ?? null,
    careSubjectType: storedState?.careSubjectType ?? locationState?.careSubjectType ?? null,
    careSubjectId: storedState?.careSubjectId ?? locationState?.careSubjectId ?? null,
    careSubjectName: storedState?.careSubjectName ?? locationState?.careSubjectName ?? "",
    careSubjectRelationship:
      storedState?.careSubjectRelationship ?? locationState?.careSubjectRelationship ?? "",
    careSubjectDateOfBirth:
      storedState?.careSubjectDateOfBirth ?? locationState?.careSubjectDateOfBirth ?? null,
    careSubjectVerificationStatus:
      storedState?.careSubjectVerificationStatus ??
      locationState?.careSubjectVerificationStatus ??
      "",
  };
}

function readBooking(id, locationState, bookingKey = "") {
  let storedState = null;
  try {
    const cleanBookingKey = String(bookingKey || "").trim();
    if (cleanBookingKey) {
      const rawByKey = sessionStorage.getItem(`afrimed:bookingKey:${cleanBookingKey}`);
      const parsedByKey = rawByKey ? safeParse(rawByKey) : null;
      if (parsedByKey) storedState = parsedByKey;

      const rawByKeyLocal = localStorage.getItem(`afrimed:bookingKey:${cleanBookingKey}`);
      const parsedByKeyLocal = rawByKeyLocal ? safeParse(rawByKeyLocal) : null;
      if (parsedByKeyLocal && !storedState) storedState = parsedByKeyLocal;
    }
    if (!storedState && id) {
      const rawById = sessionStorage.getItem(`afrimed:booking:${id}`);
      const parsedById = rawById ? safeParse(rawById) : null;
      if (parsedById) storedState = parsedById;

      const rawByIdLocal = localStorage.getItem(`afrimed:booking:${id}`);
      const parsedByIdLocal = rawByIdLocal ? safeParse(rawByIdLocal) : null;
      if (parsedByIdLocal && !storedState) storedState = parsedByIdLocal;
    }
    if (!storedState) {
      const rawLast = sessionStorage.getItem("afrimed:booking:last");
      const parsedLast = rawLast ? safeParse(rawLast) : null;
      if (parsedLast) storedState = parsedLast;
    }

    if (!storedState) {
      const rawLastLocal = localStorage.getItem("afrimed:booking:last");
      const parsedLastLocal = rawLastLocal ? safeParse(rawLastLocal) : null;
      if (parsedLastLocal) storedState = parsedLastLocal;
    }
  } catch {
    storedState = null;
  }

  if (locationState?.doctor || locationState?.doctorId || locationState?.bookingKey) {
    const merged = mergeCareSubjectFromStored(locationState, storedState);
    return {
      ...merged,
      bookingKey: String(merged?.bookingKey || bookingKey || "").trim() || null,
      doctorId: merged?.doctorId || merged?.doctor?.id || id || null,
    };
  }

  return storedState;
}

function parseAmount(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function resolveBookingAmount(booking, doctor) {
  const candidates = [
    booking?.booked_service_price,
    booking?.amountPaid,
    booking?.amount_paid,
    booking?.amount,
    booking?.servicePrice,
    booking?.service_price,
    booking?.serviceNamePrice,
    booking?.serviceLabelPrice,
    booking?.selectedService?.price,
    booking?.service?.price,
    doctor?.servicePrice,
    doctor?.service_price,
    doctor?.serviceLabelPrice,
  ];

  for (const candidate of candidates) {
    const amount = parseAmount(candidate);
    if (amount != null) return amount;
  }

  return 0;
}

function formatAmount(amount, currency = "ZAR", locale = "en-GB") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency: String(currency || "ZAR").trim(), currencyDisplay: "code" }).format(value);
}

function titleCase(value) {
  const clean = String(value || "").trim();
  if (!clean) return "Credit • Debit Card";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function paystackMethodPresentation(methodId, t = (_, fallback) => fallback) {
  switch (String(methodId || "").trim().toLowerCase()) {
    case "instant_eft": return { title: t("appointments.checkout.instantEft", "Instant EFT"), helper: t("appointments.checkout.instantEftHelp", "Pay securely through your bank.") };
    case "capitec_pay": return { title: t("appointments.checkout.capitecPay", "Capitec Pay"), helper: t("appointments.checkout.capitecPayHelp", "Approve securely in your Capitec banking app.") };
    case "qr": return { title: t("appointments.checkout.scanToPay", "Scan to Pay"), helper: t("appointments.checkout.scanToPayHelp", "Complete payment securely with the available QR payment route.") };
    case "apple_pay": return { title: t("appointments.checkout.applePay", "Apple Pay"), helper: t("appointments.checkout.applePayHelp", "Pay securely with Apple Pay on this device.") };
    case "google_pay": return { title: t("appointments.checkout.googlePay", "Google Pay"), helper: t("appointments.checkout.googlePayHelp", "Pay securely with Google Pay on this device.") };
    case "samsung_pay": return { title: t("appointments.checkout.samsungPay", "Samsung Pay"), helper: t("appointments.checkout.samsungPayHelp", "Pay securely with Samsung Pay on an eligible Samsung device.") };
    case "mtn_momo": return { title: t("appointments.checkout.mtnMomo", "MTN MoMo"), helper: t("appointments.checkout.mtnMomoHelp", "Approve the payment request on your MTN Mobile Money phone.") };
    default: return { title: t("appointments.checkout.cardMethod", "Credit • Debit Card"), helper: t("appointments.checkout.cardMethodHelp", "Pay securely with a local or international card.") };
  }
}

function localizePaymentMethodLabel(
  value,
  t = (_, fallback) => fallback,
  fallback = "",
) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) {
    return fallback || t("appointments.checkout.fields.paymentMethod", "Payment method");
  }

  if (
    raw === "card" ||
    raw === "credit" ||
    raw === "debit" ||
    raw.includes("card") ||
    raw.includes("visa") ||
    raw.includes("mastercard") ||
    raw.includes("amex")
  ) {
    return t("appointments.checkout.cardMethod", "Credit • Debit Card");
  }
  if (raw === "instant_eft" || raw === "eft" || raw.includes("instant eft")) return t("appointments.checkout.instantEft", "Instant EFT");
  if (raw === "capitec_pay") return t("appointments.checkout.capitecPay", "Capitec Pay");
  if (raw === "qr") return t("appointments.checkout.scanToPay", "Scan to Pay");
  if (raw === "apple_pay") return t("appointments.checkout.applePay", "Apple Pay");
  if (raw === "google_pay") return t("appointments.checkout.googlePay", "Google Pay");
  if (raw === "samsung_pay") return t("appointments.checkout.samsungPay", "Samsung Pay");
  if (raw === "mtn_momo") return t("appointments.checkout.mtnMomo", "MTN MoMo");
  if (raw === "bank" || raw.includes("bank") || raw.includes("transfer")) return fallback || value;
  if (raw === "cash" || raw.includes("cash")) {
    return t("appointments.confirm.payment.cash", "Cash on service");
  }
  return fallback || value;
}

function resolveInsuranceContextLabel(booking, t = (_, fallback) => fallback) {
  const existing = String(booking?.insuranceContextLabel || "").trim();
  if (existing) return existing;
  const mode = String(booking?.insurancePaymentMode || "").trim().toLowerCase();
  if (mode === "insurance") {
    const provider = String(booking?.insuranceProvider || "").trim();
    return provider
      ? t("appointments.checkout.insurance.provider", "Insurance: {provider}", { provider })
      : t("appointments.checkout.insurance.generic", "Insurance selected");
  }
  return t("appointments.checkout.insurance.selfPay", "Self-pay");
}

function formatSavedCardLabel(method = {}, t = (_, fallback) => fallback, fallbackBrand = "") {
  const brand = titleCase(method?.brand);
  const last4 = String(method?.last4 || "").trim();
  const expMonth = Number(method?.expMonth || 0);
  const expYear = Number(method?.expYear || 0);
  const month = expMonth > 0 ? String(expMonth).padStart(2, "0") : "--";
  const year = expYear > 0 ? String(expYear).slice(-2) : "--";
  return t(
    "appointments.checkout.savedCardLabel",
    "{brand} ending {last4} · expires {month}/{year}",
    {
      brand: brand || fallbackBrand || "Card",
      last4: last4 || "----",
      month,
      year,
    },
  );
}

function dedupeSavedPaymentMethods(methods = []) {
  const seen = new Set();
  return (Array.isArray(methods) ? methods : []).filter((method) => {
    const brand = String(method?.brand || "").trim().toLowerCase();
    const last4 = String(method?.last4 || "").trim();
    const key = brand && last4 ? `${brand}:${last4}` : String(method?.id || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formatCheckoutError(error, t = (_, fallback) => fallback) {
  if (error?.body?.code === "clinician_payout_not_ready") {
    return String(error?.body?.payment_unavailable_message || "").trim() ||
      t(
        "appointments.checkout.errors.clinicianPayoutPending",
        "Payment is not available yet because this clinician's payment setup is still being verified. You can continue testing the booking flow, or choose another payment option if available."
      );
  }
  const raw = String(error?.message || error || "").trim();
  const lower = raw.toLowerCase();

  if (
    lower.includes("clinician_payout_not_ready") ||
    lower.includes("payout account is still being verified") ||
    lower.includes("stripe activates transfers")
  ) {
    return t(
      "appointments.checkout.errors.clinicianPayoutPending",
      "Payment is not available yet because this clinician's payment setup is still being verified. You can continue testing the booking flow, or choose another payment option if available."
    );
  }

  if (lower.includes("no such payment_intent")) {
    return t(
      "appointments.checkout.errors.intentMissing",
      "The secure checkout session could not be found in this browser. Refresh payment details and try again."
    );
  }

  if (
    lower.includes("no such destination") ||
    lower.includes("doctor payout account must be reconnected") ||
    lower.includes("doctor_payout_reconnect_required")
  ) {
    return t(
      "appointments.checkout.errors.doctorReconnect",
      "Doctor payment setup must be reconnected before card checkout can continue. Ask the doctor to update payment preferences, then refresh payment details."
    );
  }

  if (
    lower.includes("mounted payment element") ||
    lower.includes("mounted express checkout element") ||
    lower.includes("payment element is not mounted") ||
    lower.includes("stripe.confirmpayment")
  ) {
    return t(
      "appointments.checkout.errors.cardFormLoading",
      "Secure card form is still loading. Please wait a moment, then try again."
    );
  }

  if (
    lower.includes("client secret") ||
    lower.includes("client_secret") ||
    lower.includes("does not match any associated paymentintent")
  ) {
    return t(
      "appointments.checkout.errors.prepare",
      "Secure checkout could not be prepared cleanly. Refresh payment details and try again."
    );
  }

  if (lower.includes("processing")) {
    return t(
      "appointments.checkout.errors.processing",
      "Your payment is still processing. Please wait a moment and try again."
    );
  }

  return (
    raw ||
    t(
      "appointments.checkout.errors.finalize",
      "Payment may have gone through, but we could not complete the booking. Please contact AfriMed™ support."
    )
  );
}

function buildPaymentIntentMismatchMessage(platformAccountId = "", t = (_, fallback) => fallback) {
  const accountLabel = String(platformAccountId || "").trim();
  return accountLabel
    ? t(
        "appointments.checkout.errors.intentMismatchWithAccount",
      "The secure checkout session could not be found in the active card form. Refresh payment details and try again.",
        { account: accountLabel }
      )
    : t(
        "appointments.checkout.errors.intentMismatch",
      "The secure checkout session could not be found in the active card form. Refresh payment details and try again."
      );
}

function normalizeDurationMinutes(value, fallback = 30) {
  const minutes = Number(value);
  if (Number.isFinite(minutes) && minutes > 0) return Math.round(minutes);
  return fallback;
}

function normalizeComparisonText(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesPendingBooking(pending = null, current = {}) {
  if (!pending || typeof pending !== "object") return false;

  const pendingHint =
    pending?.recoveryHint && typeof pending.recoveryHint === "object"
      ? pending.recoveryHint
      : {};

  const currentBookingKey = normalizeComparisonText(current.booking_key);
  const pendingBookingKey = normalizeComparisonText(
    pending.bookingKey || pendingHint.booking_key,
  );
  if (currentBookingKey && pendingBookingKey && currentBookingKey !== pendingBookingKey) {
    return false;
  }

  const currentDoctorId = normalizeComparisonText(current.doctor_user_id);
  const pendingDoctorId = normalizeComparisonText(
    pending.doctorUserId || pendingHint.doctor_user_id,
  );
  if (currentDoctorId && pendingDoctorId && currentDoctorId !== pendingDoctorId) {
    return false;
  }

  const currentDate = normalizeComparisonText(current.selected_date);
  const pendingDate = normalizeComparisonText(pendingHint.selected_date);
  if (currentDate && pendingDate && currentDate !== pendingDate) {
    return false;
  }

  const currentTime = normalizeComparisonText(current.selected_time);
  const pendingTime = normalizeComparisonText(pendingHint.selected_time);
  if (currentTime && pendingTime && currentTime !== pendingTime) {
    return false;
  }

  const currentServiceId = normalizeComparisonText(current.service_id);
  const pendingServiceId = normalizeComparisonText(pendingHint.service_id);
  if (currentServiceId && pendingServiceId && currentServiceId !== pendingServiceId) {
    return false;
  }

  const currentServiceName = normalizeComparisonText(current.service_name);
  const pendingServiceName = normalizeComparisonText(
    pending.serviceName || pendingHint.service_name,
  );
  if (currentServiceName && pendingServiceName && currentServiceName !== pendingServiceName) {
    return false;
  }

  return true;
}

function toAppointmentISO(dateISO, timeHHMM) {
  const date = String(dateISO || "").trim();
  const time = String(timeHHMM || "").trim();
  if (!date || !time) return null;

  const candidate = new Date(`${date}T${time}:00`);
  if (Number.isNaN(candidate.getTime())) return null;
  return candidate.toISOString();
}

function formatNotificationAppointmentDate(isoLike, locale = "en-GB") {
  const value = new Date(isoLike || "");
  if (Number.isNaN(value.getTime())) return "the selected time";
  const day = value.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
  const time = value.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} at ${time}`;
}

function persistSnapshots(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    sessionStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
    localStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    localStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    localStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
  } catch {
    // ignore snapshot persistence failures
  }
}

function CheckoutCard({ children, className = "" }) {
  return (
    <Card className={className}>
      {children}
    </Card>
  );
}

function CheckoutSummaryRow({ label, value, emphasis = false, stacked = false }) {
  if (stacked) {
    return (
      <div>
        <div className="text-body-medium text-on-surface-var">{label}</div>
        <div
          className={[
            "mt-1 text-left",
            emphasis ? "text-title-medium text-on-surface" : "text-body-large text-on-surface",
          ].join(" ")}
        >
          {value}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-body-medium text-on-surface-var">{label}</div>
      <div
        className={[
          "text-right",
          emphasis ? "text-title-medium text-on-surface" : "text-body-large text-on-surface",
        ].join(" ")}
      >
        {value}
      </div>
    </div>
  );
}

function PaystackCheckoutForm({
  t,
  amountLabel = "",
  evaluationDueLabel = "",
  appointmentLabel = "",
  insuranceContextLabel = "",
  disabled = false,
  submitting = false,
  submitError = "",
  onCheckoutPhaseChange,
  onUseSponsorPayment,
  evaluationAccessAvailable = false,
  onUseEvaluationAccess,
  careSubjectConfirmationRequired = false,
  careSubjectConfirmed = true,
  onCareSubjectConfirmedChange,
  careSubjectName = "",
  onStartPaystackPayment,
  paystackPaymentStarting = false,
  paystackPaymentStatus = "",
  onRunPaymentTest,
  paymentTestRunning = false,
  paymentTestStatus = "",
  bookingPaymentMethod = "card",
  bookingPaymentAvailable = true,
  authorizationOnly = false,
  scanToPayState = null,
  onCheckScanToPayStatus,
  onReturnFromScanToPay,
}) {
  const monetaryMethod = String(bookingPaymentMethod || "card").trim().toLowerCase() || "card";
  const monetaryPresentation = paystackMethodPresentation(monetaryMethod, t);
  const [method, setMethod] = React.useState(
    evaluationAccessAvailable ? "evaluation" : monetaryMethod,
  );
  const [phase, setPhase] = React.useState("details");
  const [testCardSelected, setTestCardSelected] = React.useState(false);
  const [capitecIdentifier, setCapitecIdentifier] = React.useState("");
  const capitecPay = monetaryMethod === "capitec_pay";
  const reviewing = phase === "review";
  const canReview = method === "evaluation"
    ? !disabled && !submitting && evaluationAccessAvailable && (!careSubjectConfirmationRequired || careSubjectConfirmed)
    : method === "sponsor"
    ? !disabled && !submitting && !paystackPaymentStarting
    : !disabled && !submitting && !paystackPaymentStarting && bookingPaymentAvailable && (!capitecPay || Boolean(capitecIdentifier.trim()));

  React.useEffect(() => {
    // Eligibility resolves after this form mounts. Promote only the untouched
    // card default; an explicit card or sponsor choice remains the patient's.
    if (evaluationAccessAvailable) {
      setMethod((current) => (current === monetaryMethod ? "evaluation" : current));
    }
  }, [evaluationAccessAvailable, monetaryMethod]);

  const footer = (action, secondary = null) => (
    <div className="pt-2 pb-[calc(88px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="md:static fixed inset-x-0 bottom-0 z-20 bg-[rgb(var(--clr-surface-cont-highest))] p-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:bg-transparent md:p-0">
        <div className="md:mx-auto md:max-w-[680px]">{action}{secondary}</div>
      </div>
    </div>
  );

  if (scanToPayState?.attemptId) {
    const failed = scanToPayState.state === "failed";
    const isCapitec = scanToPayState.method === "capitec_pay";
    const title = isCapitec ? t("appointments.checkout.capitecPay", "Capitec Pay") : t("appointments.checkout.scanToPay", "Scan to Pay");
    const pendingCopy = isCapitec
      ? t("appointments.checkout.capitecPayHelp", "Approve securely in your Capitec banking app.")
      : t("appointments.checkout.scanToPayPending", "Complete payment with your supported banking or payment app.");
    return (
      <section className="flex flex-col gap-5" aria-live="polite">
        <div>
          <div className="text-title-medium text-primary">{title}</div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            {pendingCopy}
          </div>
        </div>
        <Card className="p-5">
          <div className="flex flex-col items-center text-center">
            <div className="text-title-medium text-on-surface">{amountLabel}</div>
            {scanToPayState.imageUrl ? (
              <img
                className="mt-5 h-64 w-64 rounded-[12px] bg-white p-3 shadow-card"
                src={scanToPayState.imageUrl}
                alt={t("appointments.checkout.scanToPayQrAlt", "Transaction QR code for Scan to Pay")}
              />
            ) : null}
            {!isCapitec ? <p className="mt-5 max-w-md text-body-medium text-on-surface-var">{t("appointments.checkout.scanToPayInstruction", "Scan this QR code with your supported banking or payment app.")}</p> : null}
            <p className={[
              "mt-3 text-body-small",
              failed ? "text-[rgb(var(--schemes-critical))]" : "text-on-surface-var",
            ].join(" ")}>
              {scanToPayState.message || (failed
                ? t("appointments.checkout.scanToPayFailed", "Payment was not completed. No appointment has been confirmed.")
                : isCapitec ? t("appointments.checkout.capitecPayWaiting", "Waiting for secure Capitec Pay confirmation. Your appointment is not confirmed until payment is verified.") : t("appointments.checkout.scanToPayWaiting", "Waiting for secure payment confirmation. Your appointment is not confirmed until payment is verified."))}
            </p>
          </div>
        </Card>
        {submitError ? <div role="alert" className="afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">{submitError}</div> : null}
        {paystackPaymentStatus ? <div aria-live="polite" className="text-body-small text-on-surface-var">{paystackPaymentStatus}</div> : null}
        {footer(
          !failed ? (
            <Button type="button" className="h-12 w-full" disabled={scanToPayState.checking} onClick={onCheckScanToPayStatus}>
              {scanToPayState.checking ? t("appointments.checkout.checkingPayment", "Checking payment…") : t("appointments.checkout.checkPaymentStatus", "Check payment status")}
            </Button>
          ) : null,
          <Button type="button" variant="text" className="mt-2 h-10 w-full" onClick={onReturnFromScanToPay}>
            {t("appointments.checkout.backToPayment", "Back to payment")}
          </Button>,
        )}
      </section>
    );
  }

  if (reviewing) {
    return (
      <section className="flex flex-col gap-5">
        <div>
          <div className="text-title-medium text-primary">{t("appointments.checkout.reviewTitle", "Review & book")}</div>
          <div className="mt-2 text-body-medium text-on-surface-var">{t("appointments.checkout.reviewHelp", "Confirm the appointment and payment details before you continue.")}</div>
        </div>
        <Card className="p-5">
          <div className="space-y-4">
            <CheckoutSummaryRow label={t("appointments.checkout.fields.appointment", "Appointment")} value={appointmentLabel || t("appointments.checkout.chooseSlot", "Choose a slot")} />
            <CheckoutSummaryRow label={t("appointments.checkout.fields.paymentMethod", "Payment method")} value={method === "evaluation" ? t("appointments.checkout.evaluationAccess", "Evaluation access") : method === "sponsor" ? t("appointments.checkout.sponsorMethod", "Sponsor payment") : testCardSelected ? t("appointments.checkout.testCard", "Test card •••• 4081") : monetaryPresentation.title} />
            <CheckoutSummaryRow label={t("appointments.checkout.fields.paymentCover", "Payment cover")} value={method === "evaluation" ? t("appointments.checkout.evaluationSponsorship", "Evaluation sponsorship") : insuranceContextLabel} />
            <CheckoutSummaryRow label={method === "evaluation" ? t("appointments.checkout.serviceValue", "Service value") : t("appointments.checkout.total", "Total")} value={amountLabel} emphasis />
            {method === "evaluation" ? <CheckoutSummaryRow label={t("appointments.checkout.evaluatorDue", "Evaluator due")} value={evaluationDueLabel} emphasis /> : null}
          </div>
        </Card>
        {submitError ? <div role="alert" className="text-body-medium text-[rgb(var(--schemes-critical))]">{submitError}</div> : null}
        {footer(
          <Button
            type="button"
            className="h-12 w-full min-w-0"
            disabled={!canReview}
            onClick={() => {
              onCheckoutPhaseChange?.("payment");
              if (method === "sponsor") return onUseSponsorPayment?.();
              if (method === "evaluation") return onUseEvaluationAccess?.();
              return onStartPaystackPayment?.(capitecPay ? { key: "CELLPHONE", value: capitecIdentifier.trim() } : undefined);
            }}
          >
            {submitting || paystackPaymentStarting ? t("appointments.checkout.processing", "Processing...") : method === "sponsor" ? t("appointments.checkout.continueSponsorPayment", "Continue with sponsor payment") : method === "evaluation" ? t("appointments.checkout.bookEvaluationAppointment", "Book appointment") : t("appointments.checkout.continueSecurePayment", "Continue to secure payment")}
          </Button>,
          <Button type="button" variant="text" className="mt-2 h-10 w-full" disabled={disabled || submitting} onClick={() => { setPhase("details"); onCheckoutPhaseChange?.("details"); }}>
            {t("appointments.checkout.backToPayment", "Back to payment")}
          </Button>,
        )}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <div>
        <PaymentDetailsHeader eyebrow={t("appointments.checkout.paymentDetails", "Payment details")} title={t("appointments.checkout.chooseMethod", "How would you like to pay?")} />
        <div className="mt-4 grid gap-3">
          <CheckoutPaymentMethodRow title={monetaryPresentation.title} helper={monetaryPresentation.helper} selected={method === monetaryMethod} disabled={!bookingPaymentAvailable} onClick={() => { setMethod(monetaryMethod); setPhase("details"); onCheckoutPhaseChange?.("details"); }} />
          {!authorizationOnly && <CheckoutPaymentMethodRow title={t("appointments.checkout.sponsorMethod", "Sponsor payment")} helper={t("appointments.checkout.sponsorMethodHelp", "Send a secure payment link to a family member, guardian, or sponsor.")} selected={method === "sponsor"} onClick={() => { setMethod("sponsor"); setPhase("details"); onCheckoutPhaseChange?.("details"); }} />}
          {/* Future server-owned staging evaluator entitlement inserts Evaluation access here. */}
          {evaluationAccessAvailable ? <CheckoutPaymentMethodRow title={t("appointments.checkout.evaluationAccess", "Evaluation access")} helper={t("appointments.checkout.evaluationAccessHelp", "Available for your authorised AfriMed™ evaluation. No payment will be taken for this appointment.")} selected={method === "evaluation"} onClick={() => { setMethod("evaluation"); setPhase("details"); onCheckoutPhaseChange?.("details"); }} /> : null}
        </div>
        {method === "sponsor" ? <div className="px-4 pt-1 text-body-medium text-on-surface-var">{t("appointments.checkout.sponsorDetail", "Your selected appointment remains intact while the sponsor completes payment securely.")}</div> : method === "evaluation" ? (
          careSubjectConfirmationRequired ? <label className="flex items-start gap-3 px-4 pt-1 text-body-medium text-on-surface"><Checkbox checked={careSubjectConfirmed} onChange={onCareSubjectConfirmedChange} /><span>{t("appointments.checkout.confirmCareSubject", "I confirm this appointment is for {name}.", { name: careSubjectName || t("appointments.checkout.patientFallback", "Patient") })}</span></label> : null
        ) : (
          <>
            {capitecPay ? <label className="mt-4 block text-body-medium text-on-surface"><span>{t("appointments.checkout.capitecIdentifier", "Capitec mobile number")}</span><input className="mt-2 h-12 w-full rounded-[12px] border border-outline bg-surface px-3" type="tel" inputMode="tel" autoComplete="tel" value={capitecIdentifier} onChange={(event) => setCapitecIdentifier(event.target.value)} /><span className="mt-2 block text-body-small text-on-surface-var">{t("appointments.checkout.capitecIdentifierHelp", "Use the mobile number registered in your Capitec banking app.")}</span></label> : null}
            {!authorizationOnly && <div className="mt-4">
              <TestPaymentHelper onSelected={() => setTestCardSelected(true)} onRunTest={onRunPaymentTest} running={paymentTestRunning} />
            </div>}
            {paymentTestStatus ? <div className="mt-3 text-body-small text-on-surface-var">{paymentTestStatus}</div> : null}
          </>
        )}
        {paystackPaymentStatus ? <div className="mt-3 text-body-small text-on-surface-var">{paystackPaymentStatus}</div> : null}
        {!bookingPaymentAvailable ? <div className="mt-3 text-body-small text-[rgb(var(--schemes-critical))]">{t("appointments.checkout.errors.methodUnavailable", "This payment method is not currently available. Return to booking and choose an available secure payment method.")}</div> : null}
        {submitError ? <div className="mt-4 afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">{submitError}</div> : null}
      </div>
      {footer(<Button type="button" className="h-12 w-full min-w-0" disabled={!canReview} onClick={() => { setPhase("review"); onCheckoutPhaseChange?.("review"); }}>{t("appointments.checkout.reviewAndBook", "Review & book")}</Button>)}
    </section>
  );
}

const MTN_SANDBOX_TEST_MSISDNS = new Set(["46733123450", "46733123451", "46733123452", "46733123453", "46733123454", "56733123453"]);

function mtnMomoStatusCopy(state) {
  switch (String(state || "").toLowerCase()) {
    case "pending": return "Awaiting payer approval";
    case "failed": return "MTN reported that the payment failed. No appointment has been confirmed.";
    case "cancelled": return "Payment was declined in MTN MoMo. No appointment has been confirmed.";
    case "expired": return "The MTN MoMo payment request expired. No appointment has been confirmed.";
    default: return "Payment status is being verified. No appointment has been confirmed.";
  }
}

function MtnMomoCheckoutForm({ amountLabel, appointmentLabel, disabled, state, error, onStart, onCheck, onBack }) {
  const [localNumber, setLocalNumber] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("+27");
  const [countryIso, setCountryIso] = React.useState("za");
  const pending = state.phase === "pending";
  const terminal = ["failed", "cancelled", "expired"].includes(state.phase);
  const digits = String(localNumber || "").replace(/\D/g, "");
  const payerMsisdn = MTN_SANDBOX_TEST_MSISDNS.has(digits) ? digits : `${countryCode}${digits}`.replace(/\D/g, "");
  const sandboxFixture = MTN_SANDBOX_TEST_MSISDNS.has(payerMsisdn);
  const countryError = digits && !sandboxFixture ? "This selected country is not available for the configured MTN MoMo payment rail." : "";
  if (pending || terminal) return <section className="flex flex-col gap-5" aria-live="polite"><div><div className="flex items-center gap-2 text-title-medium text-primary"><img src="/icons/mtnmomo-logo-square-rounded.svg" alt="" className="h-6 w-6 object-contain" />MTN MoMo</div><p className="mt-2 text-body-medium text-on-surface-var">{pending ? "Approve this payment in your MTN Mobile Money app or USSD experience. Your appointment is not confirmed until AfriMed verifies it." : mtnMomoStatusCopy(state.phase)}</p></div><Card className="p-5"><CheckoutSummaryRow label="Appointment" value={appointmentLabel} /><CheckoutSummaryRow label="Total" value={amountLabel} emphasis /><CheckoutSummaryRow label="Status" value={mtnMomoStatusCopy(state.phase)} /></Card>{error ? <div role="alert" className="text-[rgb(var(--schemes-critical))]">{error}</div> : null}<Button type="button" className="h-12" disabled={disabled || state.checking} onClick={onCheck}>{state.checking ? "Checking payment…" : "Check payment status"}</Button><Button type="button" variant="text" className="h-10" disabled={disabled} onClick={onBack}>{terminal ? "Use a different payment method" : "Back to payment"}</Button></section>;
  return <section className="flex flex-col gap-5"><div><div className="flex items-center gap-2 text-title-medium text-primary"><img src="/icons/mtnmomo-logo-square-rounded.svg" alt="" className="h-6 w-6 object-contain" />MTN MoMo</div><p className="mt-2 text-body-medium text-on-surface-var">We will send an approval request to your MTN Mobile Money number. AfriMed verifies the result before confirming your appointment.</p></div><div className="flex items-end justify-between gap-4"><span className="text-label-large text-on-surface-var">Amount</span><strong className="text-title-medium text-on-surface">{amountLabel}</strong></div><div><label className="block text-label-large" htmlFor="mtn-momo-payer">MTN Mobile Money number</label><div className="mt-2"><InputPhoneSADC value={localNumber} onChange={setLocalNumber} cc={countryCode} onChangeCc={setCountryCode} countryIso={countryIso} onChangeCountry={(iso) => setCountryIso(String(iso || "").toLowerCase())} tone="surface" ariaDescribedBy="mtn-momo-payer-help" disabled={disabled || state.starting} /></div>{countryError ? <p role="alert" className="mt-2 text-body-small text-[rgb(var(--schemes-critical))]">{countryError}</p> : null}<p id="mtn-momo-payer-help" className="mt-2 text-body-small text-on-surface-var">Choose your country code and enter the mobile number. AfriMed validates the payer with the payment provider before any request is created.</p></div>{error ? <div role="alert" className="text-[rgb(var(--schemes-critical))]">{error}</div> : null}<Button type="button" className="h-12" disabled={disabled || !payerMsisdn || Boolean(countryError) || state.starting} onClick={() => onStart(payerMsisdn)}>{state.starting ? "Sending request…" : "Pay with MTN MoMo"}</Button><Button type="button" variant="text" className="h-10" disabled={disabled} onClick={onBack}>Choose a different payment method</Button></section>;
}

function CardCheckoutForm({
  t,
  checkoutPhase = "details",
  onCheckoutPhaseChange,
  amountLabel = "",
  appointmentLabel = "",
  insuranceContextLabel = "",
  disabled = false,
  loadError = "",
  submitError = "",
  rememberCard = false,
  onRememberCardChange,
  savedPaymentMethods = [],
  savedPaymentMethodsLoading = false,
  savedPaymentMethodsError = "",
  selectedSavedPaymentMethodId = "",
  useSavedPaymentMethod = false,
  onSelectSavedPaymentMethod,
  onUseNewCard,
  onSubmit,
  onRetry,
  submitting = false,
  careSubjectConfirmationRequired = false,
  careSubjectConfirmed = true,
  onCareSubjectConfirmedChange,
  careSubjectName = "",
  onUseSponsorPayment,
  evaluationAccessAvailable = false,
  onUseEvaluationAccess,
  showPaystackTestHelper = false,
  onRunPaymentTest,
  paymentTestRunning = false,
  paymentTestStatus = "",
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = React.useState(false);
  const [elementLoadError, setElementLoadError] = React.useState("");
  const [testCardSelected, setTestCardSelected] = React.useState(false);
  const hasSavedPaymentMethods = Array.isArray(savedPaymentMethods) && savedPaymentMethods.length > 0;
  // Paystack Test uses its own secure popup. Do not render a Stripe field or
  // invite browser autofill in that provider mode.
  const shouldRenderCardElement = !showPaystackTestHelper && !useSavedPaymentMethod;
  const [selectedMethod, setSelectedMethod] = React.useState(
    evaluationAccessAvailable ? "evaluation" : "card",
  );

  React.useEffect(() => {
    // Entitlement resolution is asynchronous. Promote only the untouched card
    // default; never override an explicit sponsor or card choice.
    if (evaluationAccessAvailable) {
      setSelectedMethod((current) => (current === "card" ? "evaluation" : current));
    }
  }, [evaluationAccessAvailable]);

  React.useEffect(() => {
    setElementReady(false);
    setElementLoadError("");
  }, [loadError, shouldRenderCardElement]);

  React.useEffect(() => {
    if (loadError || elementReady || !shouldRenderCardElement) return undefined;
    const timer = window.setTimeout(() => {
      setElementLoadError(
        t(
          "appointments.checkout.errors.cardFormSlow",
          "Secure card form is taking longer than expected. Refresh payment details and try again."
        )
      );
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [elementReady, loadError, shouldRenderCardElement, t]);

  const activeLoadError = loadError || elementLoadError;
  const isReviewing = checkoutPhase === "review";
  const canReviewCard =
    !disabled &&
    !submitting &&
    Boolean(stripe) &&
    Boolean(elements) &&
    (!careSubjectConfirmationRequired || careSubjectConfirmed) &&
    (!shouldRenderCardElement
      ? Boolean(selectedSavedPaymentMethodId)
      : Boolean(elementReady && !activeLoadError));
  const canContinue =
    selectedMethod === "sponsor"
      ? !disabled && !submitting
      : selectedMethod === "evaluation"
        ? !disabled && !submitting && evaluationAccessAvailable && (!careSubjectConfirmationRequired || careSubjectConfirmed)
        : canReviewCard;

  async function handlePayNow() {
    if (disabled || submitting || !stripe || !elements) return;
    if (shouldRenderCardElement && !elementReady) return;
    onCheckoutPhaseChange?.("payment");
    await onSubmit?.({ stripe, elements });
  }

  function selectMethod(nextMethod) {
    setSelectedMethod(nextMethod);
    onCheckoutPhaseChange?.("details");
  }

  function continueToReview() {
    if (!canContinue) return;
    onCheckoutPhaseChange?.("review");
  }

  const actionFooter = (action, secondary = null) => (
    <div className="pt-2 pb-[calc(88px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="md:static fixed inset-x-0 bottom-0 z-20 bg-[rgb(var(--clr-surface-cont-highest))] p-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:bg-transparent md:p-0">
        <div className="md:mx-auto md:max-w-[680px]">
          {action}
          {secondary}
        </div>
      </div>
    </div>
  );

  return (
    <section className="flex flex-col gap-5">
      <div className={isReviewing ? "hidden" : ""}>
          <PaymentDetailsHeader
            eyebrow={t("appointments.checkout.paymentDetails", "Payment details")}
            title={t("appointments.checkout.chooseMethod", "How would you like to pay?")}
          />

          <div className="mt-4 grid gap-3">
        <CheckoutPaymentMethodRow
          title={t("appointments.checkout.cardMethod", "Credit • Debit Card")}
          helper={t("appointments.checkout.cardMethodHelp", "Pay securely with a local or international card.")}
          selected={selectedMethod === "card"}
          onClick={() => selectMethod("card")}
        />
        <CheckoutPaymentMethodRow
          title={t("appointments.checkout.sponsorMethod", "Sponsor payment")}
          helper={t("appointments.checkout.sponsorMethodHelp", "Send a secure payment link to a family member, guardian, or sponsor.")}
          selected={selectedMethod === "sponsor"}
          onClick={() => selectMethod("sponsor")}
        />
        {evaluationAccessAvailable ? (
          <CheckoutPaymentMethodRow
            title={t("appointments.checkout.evaluationAccess", "Evaluation access")}
            helper={t(
              "appointments.checkout.evaluationAccessHelp",
              "Available for your authorised AfriMed™ evaluation. No payment will be taken for this appointment."
            )}
            selected={selectedMethod === "evaluation"}
            onClick={() => selectMethod("evaluation")}
          />
        ) : null}

        {selectedMethod === "sponsor" ? (
          <div className="px-4 pt-1 text-body-medium text-on-surface-var">
            {t("appointments.checkout.sponsorDetail", "Your selected appointment remains intact while the sponsor completes payment securely.")}
          </div>
        ) : selectedMethod === "evaluation" ? (
          careSubjectConfirmationRequired ? (
              <label className="flex items-start gap-3 text-body-medium text-on-surface">
                <Checkbox checked={careSubjectConfirmed} onChange={onCareSubjectConfirmedChange} />
                <span>{t("appointments.checkout.confirmCareSubject", "I confirm this appointment is for {name}.", { name: careSubjectName || t("appointments.checkout.patientFallback", "Patient") })}</span>
              </label>
          ) : null
        ) : (
          <div className="grid gap-4">
        {activeLoadError ? (
          <div className="afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
            {activeLoadError}
          </div>
        ) : null}

        {careSubjectConfirmationRequired ? (
          <div className="afrimed-card-radius bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3">
            <label className="flex items-start gap-3 text-body-medium text-on-surface">
              <Checkbox
                checked={careSubjectConfirmed}
                onChange={onCareSubjectConfirmedChange}
              />
              <span>
                {t(
                  "appointments.checkout.confirmCareSubject",
                  "I confirm this appointment is for {name}.",
                  { name: careSubjectName || t("appointments.checkout.patientFallback", "Patient") },
                )}
              </span>
            </label>
          </div>
        ) : null}

        {hasSavedPaymentMethods ? (
          <div className="grid gap-3">
            <div className="text-title-small text-on-surface-var">
              {t("appointments.checkout.savedCards", "Saved cards")}
            </div>
            {savedPaymentMethodsError ? (
              <div className="text-body-small text-[rgb(var(--schemes-critical))]">
                {savedPaymentMethodsError}
              </div>
            ) : null}
            {savedPaymentMethodsLoading ? (
              <div className="text-body-small text-on-surface-var">
                {t("appointments.checkout.loadingSavedCards", "Loading your saved cards...")}
              </div>
            ) : null}
            <div className="grid gap-3">
              {savedPaymentMethods.map((method) => {
                const isSelected = useSavedPaymentMethod && selectedSavedPaymentMethodId === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    className={[
                      "min-h-[88px] rounded-[12px] border px-4 py-3 text-left transition-colors",
                      isSelected
                        ? "border-[rgba(var(--afrimed-primary),0.72)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-1)]"
                        : "border-outline-variant bg-[rgb(var(--clr-surface))] shadow-[var(--elev-1)]",
                    ].join(" ")}
                    onClick={() => onSelectSavedPaymentMethod?.(method.id)}
                  >
                    <div className="text-title-medium text-on-surface">
                      {formatSavedCardLabel(
                        method,
                        t,
                        t("appointments.checkout.cardBrandFallback", "Card")
                      )}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {t(
                        "appointments.checkout.useSavedCardHint",
                        "Use this saved card without typing the number again."
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-start">
              <Button
                type="button"
                variant="tonal"
                className="h-11 self-start px-4"
                onClick={onUseNewCard}
                disabled={disabled || submitting}
              >
                {t("appointments.checkout.useDifferentCard", "Use a different card")}
              </Button>
            </div>
          </div>
        ) : null}

        {!hasSavedPaymentMethods && savedPaymentMethodsLoading ? (
          <div className="text-body-small text-on-surface-var">
            {t("appointments.checkout.loadingSavedCards", "Loading your saved cards...")}
          </div>
        ) : null}

        {!hasSavedPaymentMethods && savedPaymentMethodsError ? (
          <div className="text-body-small text-[rgb(var(--schemes-critical))]">
            {savedPaymentMethodsError}
          </div>
        ) : null}

        {shouldRenderCardElement ? (
          <div className="rounded-[12px] bg-[rgb(var(--clr-surface-container-low))] p-4 shadow-[var(--elev-1)]">
            {!elementReady && !activeLoadError ? (
              <div className="mb-3 text-body-small text-on-surface-var">
                {t("appointments.checkout.loadingCardForm", "Loading secure card form...")}
              </div>
            ) : null}
            {activeLoadError ? (
              <div className="grid gap-3">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_132px]">
                  <div className="h-[44px] rounded-[12px] bg-[rgb(var(--clr-surface-container-lowest))]" />
                  <div className="h-[44px] rounded-[12px] bg-[rgb(var(--clr-surface-container-lowest))]" />
                </div>
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                  <div className="h-[44px] rounded-[12px] bg-[rgb(var(--clr-surface-container-lowest))]" />
                  <div className="h-[44px] rounded-[12px] bg-[rgb(var(--clr-surface-container-lowest))]" />
                </div>
              </div>
            ) : (
              <CardElement
                onReady={() => {
                  setElementReady(true);
                  setElementLoadError("");
                }}
                onChange={(event) => {
                  if (event?.error?.message) {
                    setElementLoadError(formatCheckoutError(event.error, t));
                    return;
                  }
                  setElementLoadError("");
                }}
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      color: "#111827",
                      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                      fontSize: "16px",
                      "::placeholder": {
                        color: "#8a96a8",
                      },
                      iconColor: "#667692",
                    },
                    invalid: {
                      color: "#f8533a",
                      iconColor: "#f8533a",
                    },
                  },
                }}
              />
            )}
          </div>
        ) : null}

        {showPaystackTestHelper ? (
          <>
            <div className="mt-4">
              <TestPaymentHelper
                onSelected={() => setTestCardSelected(true)}
                onRunTest={onRunPaymentTest}
                running={paymentTestRunning}
              />
            </div>
            {paymentTestStatus ? <div className="text-body-small text-on-surface-var">{paymentTestStatus}</div> : null}
          </>
        ) : null}

        {!showPaystackTestHelper ? <label className="flex min-w-0 items-center gap-3 text-body-medium text-on-surface-var">
          <Checkbox className="shrink-0" checked={rememberCard} onChange={onRememberCardChange} />
          <span>
            {t(
              "appointments.checkout.rememberCard",
              "Remember credit card details for next time"
            )}
          </span>
        </label> : null}

        {(activeLoadError || submitError) ? (
        <div className="flex justify-start pt-1">
          <Button
            type="button"
            variant="tonal"
            className="h-11 self-start px-4"
            onClick={onRetry}
            disabled={submitting}
          >
            {t("appointments.checkout.refresh", "Refresh payment details")}
          </Button>
        </div>) : null}

        {submitError ? (
          <div className="afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
            {submitError}
          </div>
        ) : null}
          </div>
        )}
          </div>

          {actionFooter(
            <Button
              type="button"
              className="h-12 w-full min-w-0"
              disabled={!canContinue}
              onClick={continueToReview}
            >
              {t("appointments.checkout.reviewAndBook", "Review & book")}
            </Button>,
          )}
      </div>
      {isReviewing ? (
        <>
          <div>
            <div className="text-title-medium text-primary">
              {t("appointments.checkout.reviewTitle", "Review & book")}
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t("appointments.checkout.reviewHelp", "Confirm the appointment and payment details before you continue.")}
            </div>
          </div>

          <Card className="p-5">
            <div className="space-y-4">
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.appointment", "Appointment")}
                value={appointmentLabel || t("appointments.checkout.chooseSlot", "Choose a slot")}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.paymentMethod", "Payment method")}
                value={selectedMethod === "sponsor" ? t("appointments.checkout.sponsorMethod", "Sponsor payment") : selectedMethod === "evaluation" ? t("appointments.checkout.evaluationAccess", "Evaluation access") : testCardSelected ? "Test card •••• 4081" : t("appointments.checkout.cardMethod", "Credit • Debit Card")}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.paymentCover", "Payment cover")}
                value={selectedMethod === "evaluation" ? t("appointments.checkout.evaluationSponsorship", "Evaluation sponsorship") : insuranceContextLabel}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.total", "Total")}
                value={amountLabel}
                emphasis
              />
            </div>
          </Card>

          {selectedMethod === "evaluation" ? (
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.checkout.evaluationReviewHelp", "No payment will be taken for this authorised evaluation.")}
            </div>
          ) : null}

          {actionFooter(
            <Button
              type="button"
              className="h-12 w-full min-w-0"
              disabled={selectedMethod === "sponsor" ? disabled || submitting : selectedMethod === "evaluation" ? !canContinue : !canReviewCard}
              onClick={() => {
                onCheckoutPhaseChange?.("payment");
                if (selectedMethod === "sponsor") return onUseSponsorPayment?.();
                if (selectedMethod === "evaluation") return onUseEvaluationAccess?.();
                return handlePayNow();
              }}
            >
              {submitting
                ? t("appointments.checkout.processing", "Processing...")
                : selectedMethod === "sponsor"
                  ? t("appointments.checkout.continueSponsorPayment", "Continue with sponsor payment")
                  : selectedMethod === "evaluation"
                    ? t("appointments.checkout.bookEvaluationAppointment", "Book appointment")
                  : t("appointments.checkout.payAndBook", "Pay and book")}
            </Button>,
            <Button
              type="button"
              variant="text"
              className="mt-2 h-10 w-full"
              disabled={disabled || submitting}
              onClick={() => onCheckoutPhaseChange?.("details")}
            >
              {t("appointments.checkout.backToPayment", "Back to payment")}
            </Button>,
          )}
        </>
      ) : null}
    </section>
  );
}

function CheckoutStatusCard({ children, tone = "default" }) {
  const toneClass =
    tone === "critical"
      ? "bg-[var(--state-critical-08)] text-[rgb(var(--schemes-critical))]"
      : "text-on-surface-var";

  return (
    <Card className={`px-5 py-5 text-body-medium ${toneClass}`}>
      {children}
    </Card>
  );
}

function CheckoutValueBlock({ label, value, helper }) {
  return (
    <div>
      <div className="text-label-large text-on-surface-var">{label}</div>
      <div className="mt-2 text-title-medium text-on-surface">{value}</div>
      {helper ? <div className="mt-2 text-body-small text-on-surface-var">{helper}</div> : null}
    </div>
  );
}

function DoctorTermsDisclosureCard({
  t,
  doctorTermsUrl = "",
  doctorTermsUpdatedAt = "",
}) {
  const hasTerms = String(doctorTermsUrl || "").trim().length > 0;
  const updatedDate = doctorTermsUpdatedAt ? new Date(doctorTermsUpdatedAt) : null;
  const updatedLabel =
    updatedDate && !Number.isNaN(updatedDate.getTime())
      ? updatedDate.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";

  return (
    <div>
      <div className="text-label-large text-on-surface-var">
        {t("appointments.checkout.doctorTerms.title", "Doctor • facility terms")}
      </div>
      <div className="mt-2 text-title-medium text-on-surface">
        {hasTerms
          ? t(
              "appointments.checkout.doctorTerms.available",
              "Separate doctor terms available"
            )
          : t(
              "appointments.checkout.doctorTerms.none",
              "No separate doctor terms uploaded"
            )}
      </div>
      <div className="mt-2 text-body-small text-on-surface-var">
        {hasTerms
          ? t(
              "appointments.checkout.doctorTerms.availableHelp",
              "These terms belong to the doctor or facility and are separate from AfriMed™ platform terms."
            )
          : t(
              "appointments.checkout.doctorTerms.noneHelp",
              "AfriMed™ platform terms still apply. This doctor has not uploaded an additional facility-specific terms document yet."
            )}
      </div>
      {hasTerms ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-body-small text-on-surface-var">
            {updatedLabel
              ? t("appointments.checkout.doctorTerms.updated", "Updated {date}", {
                  date: updatedLabel,
                })
              : t("appointments.checkout.doctorTerms.ready", "Ready to review")}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="h-10"
            onClick={() => window.open(doctorTermsUrl, "_blank", "noopener,noreferrer")}
          >
            {t("appointments.checkout.doctorTerms.view", "View doctor T&Cs")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
const REMEMBER_CARD_STORAGE_KEY = "afrimed:checkout:remember-card";

function CheckoutStepper({ activeStep = 1, steps = [], showActive = true }) {
  return (
    <div className="flex w-full items-center gap-2">
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div className="flex min-w-0 items-center gap-2">
            {index < activeStep ? (
              <img
                src="/icons/check-ok.svg"
                alt=""
                aria-hidden="true"
                className="h-5 w-5 shrink-0"
              />
            ) : (
              <div
                className={[
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-label-large",
                  showActive && index === activeStep
                    ? "bg-[rgba(var(--afrimed-primary),0.14)] text-primary"
                    : "bg-[rgba(var(--clr-outline-variant),0.55)] text-on-surface-var",
                ].join(" ")}
              >
                {index + 1}
              </div>
            )}
            <div
              className={[
                "truncate text-body-small",
                showActive && index === activeStep ? "text-on-surface" : "text-on-surface-var",
              ].join(" ")}
            >
              {label}
            </div>
          </div>
          {index < steps.length - 1 ? (
            <div
              className={[
                "h-px flex-1",
                index < activeStep
                  ? "bg-[rgba(var(--afrimed-primary),0.45)]"
                  : "bg-[rgba(var(--clr-outline-variant),0.9)]",
              ].join(" ")}
            />
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

function CheckoutSidebarSummary({
  t,
  appointmentLabel,
  amountLabel,
  careSubjectType,
  doctorDisplayName,
  doctorTermsUpdatedAt,
  doctorTermsUrl,
  methodsError,
  onPrepareSponsorPayment,
  paymentLabel,
  insuranceContextLabel,
  patientName,
  responsibleAdultName,
  serviceName,
  submitError,
}) {
  return (
    <div className="flex flex-col gap-5">
      <CheckoutCard className="px-5 py-5">
        <div className="grid gap-5 md:grid-cols-2 md:gap-x-8 lg:grid-cols-3">
          <div>
            <div className="text-label-large text-primary">
              {t("appointments.checkout.summary", "Summary")}
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t(
                "appointments.checkout.summaryHelp",
                "Keep the selected slot intact while you confirm payment."
              )}
            </div>

            <div className="mt-5 border-b border-outline-variant pb-5 lg:border-b-0 lg:pb-0">
              <div className="text-label-large text-on-surface-var">
                {t("appointments.checkout.total", "Total")}
              </div>
              <div className="mt-2 text-title-large text-on-surface">{amountLabel}</div>
            </div>

            <div className="mt-5 space-y-4">
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.doctor", "Clinician")}
                value={doctorDisplayName || t("appointments.checkout.doctorFallback", "AfriMed™ doctor")}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.service", "Service")}
                value={serviceName}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.patient", "Patient")}
                value={patientName || t("appointments.checkout.patientFallback", "Patient")}
              />
              {careSubjectType === "dependant" ? (
                <CheckoutSummaryRow
                  label={t("appointments.checkout.fields.responsibleAdult", "Responsible adult")}
                  value={responsibleAdultName || patientName || t("appointments.checkout.patientFallback", "Patient")}
                />
              ) : null}
            </div>
          </div>

          <div className="border-t border-outline-variant pt-5 md:border-l md:border-t-0 md:pl-8 md:pt-0">
            <div className="space-y-4">
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.appointment", "Appointment")}
                value={appointmentLabel || t("appointments.checkout.chooseSlot", "Choose a slot")}
                stacked
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.paymentMethod", "Payment method")}
                value={paymentLabel}
              />
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.paymentCover", "Payment cover")}
                value={insuranceContextLabel}
              />
            </div>
          </div>

          <div className="border-t border-outline-variant pt-5 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
            {onPrepareSponsorPayment && <CheckoutValueBlock
              label={t("appointments.checkout.sponsorOption", "Sponsor option")}
              value={t("appointments.checkout.sponsorTitle", "Use a secure sponsor link")}
              helper={t(
                "appointments.checkout.sponsorHelp",
                "Family, guardians, or diaspora sponsors can fund this visit without the patient restarting the booking."
              )}
            />}

            <div className="mt-5">
              <DoctorTermsDisclosureCard
                t={t}
                doctorTermsUrl={doctorTermsUrl}
                doctorTermsUpdatedAt={doctorTermsUpdatedAt}
              />
            </div>
          </div>
        </div>
      </CheckoutCard>

      {methodsError ? (
        <div className="afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
          {methodsError}
        </div>
      ) : null}
    </div>
  );
}

function CheckoutSummaryMobile({
  t,
  amountLabel,
  appointmentLabel,
  careSubjectType,
  doctorDisplayName,
  doctorTermsUpdatedAt,
  doctorTermsUrl,
  methodsError,
  open,
  onChooseDifferentMethod,
  onPrepareSponsorPayment,
  onToggle,
  paymentLabel,
  insuranceContextLabel,
  patientName,
  responsibleAdultName,
  serviceName,
  submitError,
}) {
  return (
    <CheckoutCard className="md:hidden">
      <div className="flex items-center justify-between gap-4 px-4 py-4">
        <div className="min-w-0">
          <div className="text-label-large text-on-surface-var">
            {t("appointments.checkout.selectedAppointment", "Selected appointment")}
          </div>
          <div className="mt-1 text-title-medium text-on-surface">{amountLabel}</div>
        </div>
        <Button type="button" variant="text" size="sm" className="shrink-0" onClick={onToggle}>
          {open
            ? t("appointments.checkout.hideDetails", "Hide details")
            : t("appointments.checkout.viewDetails", "View details")}
        </Button>
      </div>
      {open ? (
        <div className="border-t border-outline-variant px-4 py-4">
          <div className="space-y-4">
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.doctor", "Clinician")}
              value={doctorDisplayName || t("appointments.checkout.doctorFallback", "AfriMed™ doctor")}
            />
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.service", "Service")}
              value={serviceName}
            />
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.patient", "Patient")}
              value={patientName || t("appointments.checkout.patientFallback", "Patient")}
            />
            {careSubjectType === "dependant" ? (
              <CheckoutSummaryRow
                label={t("appointments.checkout.fields.responsibleAdult", "Responsible adult")}
                value={responsibleAdultName || patientName || t("appointments.checkout.patientFallback", "Patient")}
              />
            ) : null}
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.appointment", "Appointment")}
              value={appointmentLabel || t("appointments.checkout.chooseSlot", "Choose a slot")}
            />
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.paymentMethod", "Payment method")}
              value={paymentLabel}
            />
            <CheckoutSummaryRow
              label={t("appointments.checkout.fields.paymentCover", "Payment cover")}
              value={insuranceContextLabel}
            />
          </div>
          <div className="mt-4">
            <DoctorTermsDisclosureCard
              t={t}
              doctorTermsUrl={doctorTermsUrl}
              doctorTermsUpdatedAt={doctorTermsUpdatedAt}
            />
          </div>
          {submitError ? (
            <div className="mt-4 afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {submitError}
            </div>
          ) : null}
          {methodsError ? (
            <div className="mt-4 afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {methodsError}
            </div>
          ) : null}
        </div>
      ) : null}
    </CheckoutCard>
  );
}

function WorldlineSponsorLinkDialog({ open, onClose, onCreate, onRevoke, state, t }) {
  const panelRef = React.useRef(null);
  const openerRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timeout = window.setTimeout(() => panelRef.current?.querySelector("button")?.focus(), 0);
    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll('button:not([disabled]), [href], input:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      openerRef.current?.focus?.();
    };
  }, [onClose, open]);
  if (!open) return null;
  const hasLink = Boolean(state?.url);
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <section ref={panelRef} className="relative w-full max-w-[560px] rounded-[28px] bg-[rgb(var(--clr-surface-cont-highest))] p-6 shadow-card" role="dialog" aria-modal="true" aria-labelledby="worldline-sponsor-title">
        <h2 id="worldline-sponsor-title" className="text-headline-small text-on-surface">{t("worldline.sponsor.dialogTitle", "Secure sponsor link")}</h2>
        <p className="mt-3 text-body-medium text-on-surface-var">{t("worldline.sponsor.dialogBody", "Create a one-time, time-limited payment link for this healthcare appointment. The link does not reveal patient details.")}</p>
        {state?.error ? <p className="mt-4 rounded-xl bg-[var(--state-critical-08)] p-3 text-body-small text-[rgb(var(--schemes-critical))]">{state.error}</p> : null}
        {hasLink ? <div className="mt-5 rounded-xl bg-[rgb(var(--clr-surface-container))] p-4">
          <p className="break-all text-body-small text-on-surface">{state.url}</p>
          <p className="mt-2 text-body-small text-on-surface-var">{t("worldline.sponsor.expires", "Expires")}: {state.expiresLabel}</p>
        </div> : null}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {hasLink ? <Button size="lg" onClick={() => void navigator.clipboard?.writeText(state.url)}>{t("worldline.sponsor.copy", "Copy secure link")}</Button> : <Button size="lg" loading={state?.creating} disabled={state?.creating} onClick={onCreate}>{t("worldline.sponsor.generate", "Generate secure link")}</Button>}
          {hasLink ? <Button size="lg" variant="tonal" loading={state?.revoking} disabled={state?.revoking} onClick={onRevoke}>{t("worldline.sponsor.revoke", "Revoke link")}</Button> : <Button size="lg" variant="tonal" onClick={onClose}>{t("common.cancel", "Cancel")}</Button>}
        </div>
        {hasLink ? <Button className="mt-3" fullWidth variant="text" onClick={onClose}>{t("common.done", "Done")}</Button> : null}
      </section>
    </div>
  );
}

export default function AppointmentPay() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t, languageCode, formatDate } = useLanguage();
  const [worldlineStarting, setWorldlineStarting] = React.useState(false);
  const [worldlineEligible, setWorldlineEligible] = React.useState(false);
  const worldlineIdempotencyRef = React.useRef("");
  const [worldlineSponsorDialogOpen, setWorldlineSponsorDialogOpen] = React.useState(false);
  const [worldlineSponsorLink, setWorldlineSponsorLink] = React.useState({ url: "", sponsorLinkId: "", expiresLabel: "", creating: false, revoking: false, error: "" });

  const sponsorReturnParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const bookingKeyParam = React.useMemo(
    () =>
      String(
        sponsorReturnParams.get("booking_key") || location.state?.bookingKey || "",
      ).trim(),
    [location.state?.bookingKey, sponsorReturnParams],
  );
  const booking = React.useMemo(() => {
    const resolved = readBooking(id, location.state, bookingKeyParam);
    if (!resolved) return resolved;
    const paymentMethod = normalizeDoctorPaymentMethodId(resolved.paymentMethod);
    return paymentMethod ? { ...resolved, paymentMethod } : resolved;
  }, [bookingKeyParam, id, location.state]);
  const sponsorSessionId = React.useMemo(
    () => String(sponsorReturnParams.get("session_id") || "").trim(),
    [sponsorReturnParams],
  );
  const bookingDoctorId = String(
    id || booking?.doctorId || booking?.doctor?.id || "",
  ).trim();
  const pendingMatchHint = React.useMemo(
    () => ({
      booking_key: booking?.bookingKey || bookingKeyParam,
      doctor_user_id: bookingDoctorId,
      selected_date: booking?.selectedDate || "",
      selected_time: booking?.selectedTime || "",
      service_id: booking?.serviceId || booking?.selectedServiceId || "",
      service_name:
        booking?.serviceName ||
        booking?.doctor?.serviceLabel ||
        booking?.doctor?.serviceName ||
        "",
    }),
    [
      booking?.doctor?.serviceLabel,
      booking?.doctor?.serviceName,
      booking?.bookingKey,
      booking?.selectedDate,
      booking?.selectedServiceId,
      booking?.selectedTime,
      booking?.serviceId,
      booking?.serviceName,
      bookingKeyParam,
      bookingDoctorId,
    ],
  );
  const [checkoutRefreshKey, setCheckoutRefreshKey] = React.useState(0);
  const pendingSponsorRecovery = React.useMemo(() => {
    const authUserId = String(auth?.user?.id || "").trim();
    const pending = readPendingSponsorPayment(authUserId, "appointment_checkout");
    if (!pending || !String(pending.paymentLinkId || "").trim()) return null;
    if (!matchesPendingBooking(pending, pendingMatchHint)) {
      return null;
    }
    return pending;
  }, [auth?.user?.id, checkoutRefreshKey, pendingMatchHint]);
  const isSponsorReturn = React.useMemo(
    () =>
      (String(sponsorReturnParams.get("sponsor_payment") || "").trim() === "1" ||
        !!String(pendingSponsorRecovery?.paymentLinkId || "").trim()) &&
      (!!sponsorSessionId || !!String(pendingSponsorRecovery?.paymentLinkId || "").trim()),
    [pendingSponsorRecovery?.paymentLinkId, sponsorReturnParams, sponsorSessionId],
  );

  const doctor = booking?.doctor || null;
  const { name: doctorDisplayName } = usePatientFacingClinicianName({
    doctor,
    doctorId: bookingDoctorId,
    doctorName: booking?.doctorName || booking?.doctor_name,
  });
  const amountPaid = React.useMemo(
    () => resolveBookingAmount(booking, doctor),
    [booking, doctor],
  );
  const currency = String(
    booking?.currency || doctor?.currency || "ZAR",
  ).trim() || "ZAR";
  const paymentMethodId = String(booking?.paymentMethod || "").trim().toLowerCase();
  const requiresCardCheckout = isCardPaymentMethod(paymentMethodId);
  const paymentProviderMode = activePaymentProviderMode();
  const isPaystackProvider = isPaystackRuntime();
  const isPaystackTest = isPaystackTestRuntime();
  const isPaystackTestHelper = isPaystackTestHelperRuntime();
  const isWorldlineProvider = isWorldlineRuntime();
  const serviceName = String(
    booking?.serviceName ||
      doctor?.serviceLabel ||
      doctor?.serviceName ||
      t("appointments.confirm.serviceFallback", "Consultation"),
  ).trim();
  const bookingDurationMinutes = normalizeDurationMinutes(
    booking?.durationMinutes ?? booking?.duration_minutes ?? booking?.duration,
    30,
  );
  const consultationType =
    booking?.consultationType ||
    booking?.consultation_type ||
    (booking?.mode === "evisit" ? "private" : "moh");
  const guardianEmail = String(
    auth?.user?.email || auth?.profile?.email || "",
  ).trim();
  const guardianName = String(
    auth?.profile?.full_name ||
      auth?.profile?.display_name ||
      auth?.user?.user_metadata?.full_name ||
      t("appointments.confirm.patientFallback", "Patient"),
  ).trim() || t("appointments.confirm.patientFallback", "Patient");
  const careSubject = React.useMemo(
    () => normalizeCareSubjectFromBooking(booking || {}, auth),
    [auth, booking],
  );
  const patientName = String(careSubject?.patientName || guardianName).trim() || guardianName;
  const patientDisplayName = React.useMemo(
    () => String(careSubject?.type || "self").trim().toLowerCase() === "dependant"
      ? patientName
      : resolvePatientDisplayName({ profile: auth?.profile, user: auth?.user, fallback: patientName }),
    [auth?.profile, auth?.user, careSubject?.type, patientName],
  );
  const responsibleAdultName =
    String(careSubject?.guardianName || guardianName).trim() || guardianName;
  const careSubjectConfirmationRequired =
    String(careSubject?.type || "self").trim().toLowerCase() === "dependant" &&
    Boolean(String(patientName || "").trim());
  const [careSubjectConfirmed, setCareSubjectConfirmed] = React.useState(
    !careSubjectConfirmationRequired,
  );

  const [methodsState, setMethodsState] = React.useState({
    loading: false,
    error: "",
    methods: [],
  });
  const [intentState, setIntentState] = React.useState({
    loading: false,
    error: "",
    clientSecret: "",
    paymentIntentId: "",
    platformAccountId: "",
    publishableKey: "",
    livemode: null,
    applicationFeeAmount: 0,
    doctorNetAmount: 0,
    currency: currency || "ZAR",
    customerReused: false,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  // Payment details, review, and payment execution are distinct presentation
  // states. This state drives only the checkout progress indicator.
  const [checkoutPhase, setCheckoutPhase] = React.useState("details");
  const [mobileSummaryOpen, setMobileSummaryOpen] = React.useState(false);
  const [stripeState, setStripeState] = React.useState({
    loading: requiresCardCheckout,
    error: "",
  });
  const [evaluationAccessState, setEvaluationAccessState] = React.useState({
    loading: true,
    available: false,
  });
  const evaluationIdempotencyRef = React.useRef("");
  const paymentCompletedRef = React.useRef(false);
  const checkoutClickedRef = React.useRef(false);
  const logPaymentEvent = React.useCallback(
    (eventType, metadata = {}) => {
      const userId = String(auth?.user?.id || "").trim();
      if (!userId) return;
      void writeUserActivityEvent({
        userId,
        role: "patient",
        route: `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`,
        eventType,
        metadata: {
          doctor_id: bookingDoctorId || null,
          booking_key: booking?.bookingKey || bookingKeyParam || null,
          requires_card_checkout: requiresCardCheckout,
          ...metadata,
        },
      }).catch(() => {});
    },
    [
      auth?.user?.id,
      booking?.bookingKey,
      bookingDoctorId,
      bookingKeyParam,
      location.hash,
      location.pathname,
      location.search,
      requiresCardCheckout,
    ],
  );

  const paymentRouteKey = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;

  React.useEffect(() => {
    let active = true;
    setEvaluationAccessState({ loading: true, available: false });
    getEvaluationAccessEligibility()
      .then((result) => {
        if (active) setEvaluationAccessState({ loading: false, available: result?.available === true });
      })
      .catch(() => {
        if (active) setEvaluationAccessState({ loading: false, available: false });
      });
    return () => {
      active = false;
    };
  }, [auth?.user?.id]);

  React.useEffect(() => {
    logPaymentEvent("payment_screen_loaded", {
      has_booking: Boolean(booking),
      payment_method: paymentMethodId || "",
      sponsor_return: isSponsorReturn,
    });
    return () => {
      if (!paymentCompletedRef.current) {
        logPaymentEvent("user_abandoned_payment_screen", {
          checkout_clicked: checkoutClickedRef.current,
          sponsor_return: isSponsorReturn,
        });
      }
    };
  }, [paymentRouteKey]);
  const [rememberCard, setRememberCard] = React.useState(() => {
    try {
      return localStorage.getItem(REMEMBER_CARD_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [savedCardsState, setSavedCardsState] = React.useState({
    loading: false,
    error: "",
    customerId: "",
    methods: [],
  });
  const [doctorTermsState, setDoctorTermsState] = React.useState({
    doctorTermsUrl: "",
    doctorTermsUpdatedAt: "",
  });
  const [selectedSavedPaymentMethodId, setSelectedSavedPaymentMethodId] = React.useState("");
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = React.useState(false);
  const [sponsorReturnState, setSponsorReturnState] = React.useState({
    loading: false,
    error: "",
  });
  const [paymentTestState, setPaymentTestState] = React.useState({ running: false, message: "" });
  const [paystackAppointmentState, setPaystackAppointmentState] = React.useState({ starting: false, message: "" });
  const [scanToPayState, setScanToPayState] = React.useState(null);
  const [mtnMomoState, setMtnMomoState] = React.useState({ phase: "details", attemptId: "", starting: false, checking: false, message: "" });
  const scanToPayVerificationRef = React.useRef(false);
  const paystackAppointmentIdempotencyRef = React.useRef({ method: "", key: "" });
  const paystackExternalHandoffRef = React.useRef(false);
  const [supportedProviderMethods, setSupportedProviderMethods] = React.useState([]);
  const [authorizationOnly, setAuthorizationOnly] = React.useState(false);

  const completeVerifiedAppointmentJourney = React.useCallback(async ({ appointmentId, paymentLabel: verifiedPaymentLabel, paymentIntentId = "" }) => {
    const id = String(appointmentId || "").trim();
    if (!id) return;
    paymentCompletedRef.current = true;
    const reminder = await persistPatientAppointmentReminderDefault(auth?.user?.id).catch(() => ({ defaulted: false, updatedAt: null }));
    const successState = {
      doctor, appointmentId: id, amountPaid, currency, careSubjectType: careSubject?.type || "self", careSubjectId: careSubject?.dependantId || careSubject?.selectionId || null,
      careSubjectName: patientName, careSubjectRelationship: careSubject?.relationship || "", careSubjectDateOfBirth: careSubject?.dateOfBirth || null,
      guardianUserId: auth?.user?.id || null, guardianName, patientName, patient_name: patientName,
      patient: { name: patientName, guardian_name: guardianName, care_subject_type: careSubject?.type || "self", care_subject_relationship: careSubject?.relationship || "", responsible_relationship: careSubject?.relationship || "" },
      paymentLabel: verifiedPaymentLabel || localizePaymentMethodLabel(paymentMethodId, t), paymentIntentId,
      insurancePaymentMode: booking?.insurancePaymentMode || "self_pay", insuranceProvider: booking?.insuranceProvider || "", insuranceContextLabel: booking?.insuranceContextLabel || "Self-pay", insuranceProviderOptions: booking?.insuranceProviderOptions || [], insuranceAcceptedLabel: booking?.insuranceAcceptedLabel || "",
      serviceId: booking?.serviceId || booking?.selectedServiceId || null, serviceName, serviceNamePrice: formatAmount(amountPaid, currency, languageCode), selectedDate: booking?.selectedDate, selectedTime: booking?.selectedTime, mode: booking?.mode || null,
      facilityId: booking?.facilityId || null, facilityName: booking?.facilityName || "", facility: booking?.facility || null, consultation_type: consultationType,
      practice_id: booking?.facilityId || booking?.facility?.id || doctor?.facility_id || doctor?.practice_id || null,
      when: { dateISO: booking?.selectedDate, time: booking?.selectedTime, duration_mins: bookingDurationMinutes }, duration: bookingDurationMinutes, durationMinutes: bookingDurationMinutes, duration_minutes: bookingDurationMinutes,
      appointmentRemindersDefaulted: reminder.defaulted, appointmentReminderRevision: reminder.updatedAt || null, appointmentStatus: "pending",
    };
    persistSnapshots(id, successState);
    window.dispatchEvent(new Event("afrimed:appointments:changed"));
    window.dispatchEvent(new Event("afrimed:notifications:changed"));
    window.dispatchEvent(new Event("afrimed:transactions:changed"));
    nav("/appointments/success", { state: successState, replace: true });
  }, [amountPaid, auth?.user?.id, booking, bookingDurationMinutes, careSubject, consultationType, currency, doctor, guardianName, nav, patientName, paymentMethodId, serviceName, t]);

  React.useEffect(() => {
    let alive = true;
    void loadSupportedAppointmentPaymentMethods({ forClinicianSetup: true, serviceId: booking?.serviceId || booking?.selectedServiceId, includeContext: true }).then((snapshot) => {
      if (alive) { setSupportedProviderMethods(snapshot.methods); setAuthorizationOnly(snapshot.authorizationOnly); }
    }).catch(() => { if (alive) setSupportedProviderMethods([]); });
    return () => { alive = false; };
  }, [booking?.serviceId, booking?.selectedServiceId]);

  const mtnClinicianUserId = String(booking?.doctorUserId || booking?.doctor_user_id || booking?.doctor?.user_id || bookingDoctorId || "").trim();
  const mtnServiceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
  const mtnStartsAt = String(booking?.mtnAttemptStartsAt || booking?.starts_at || "").trim() || toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
  const mtnMomoCheckingRef = React.useRef(false);
  const mtnPhase = (value) => ["failed", "cancelled", "expired"].includes(String(value || "").toLowerCase()) ? String(value).toLowerCase() : "pending";
  const mtnMessage = (value) => mtnMomoStatusCopy(value);
  async function checkMtnMomoStatus(attemptId = mtnMomoState.attemptId) { const id = String(attemptId || "").trim(); if (!id || mtnMomoCheckingRef.current) return; mtnMomoCheckingRef.current = true; setMtnMomoState((v) => ({ ...v, checking: true, message: "Checking secure payment status…" })); try { const result = await verifyMtnMomoAppointmentPayment({ attempt_id: id }); if (result?.state === "paid" && result?.appointment_id) { await completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentLabel: "MTN MoMo" }); return; } const next = String(result?.state || "pending").toLowerCase(); setMtnMomoState((v) => ({ ...v, attemptId: id, checking: false, phase: mtnPhase(next), message: mtnMessage(next) })); } catch { setMtnMomoState((v) => ({ ...v, checking: false, message: "We couldn’t verify payment yet. Your appointment is not confirmed." })); } finally { mtnMomoCheckingRef.current = false; } }
  async function recoverMtnMomoAfterTransportTimeout() { setMtnMomoState((v) => ({ ...v, starting: false, phase: "pending", message: "Payment pending. We’re confirming your MTN MoMo payment request." })); try { const result = await recoverMtnMomoAppointmentPayment({ clinician_user_id: mtnClinicianUserId, service_id: mtnServiceId, starts_at: mtnStartsAt }); if (result?.state === "paid" && result?.appointment_id) { await completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentLabel: "MTN MoMo" }); return; } const next = String(result?.state || "pending").toLowerCase(); setMtnMomoState((v) => ({ ...v, attemptId: String(result?.attempt_id || v.attemptId || ""), starting: false, checking: false, phase: mtnPhase(next), message: mtnMessage(next) })); } catch { setMtnMomoState((v) => ({ ...v, starting: false, checking: false, phase: "pending", message: "Payment pending. We’re confirming your MTN MoMo payment request." })); } }
  async function handleStartMtnMomoPayment(payerMsisdn) { if (mtnMomoState.starting || submitting) return; if (!mtnClinicianUserId || !mtnServiceId || !mtnStartsAt) { setSubmitError("Your booking details are incomplete. Return and choose a time again."); return; } try { setSubmitError(""); setMtnMomoState((v) => ({ ...v, starting: true, message: "" })); const result = await initializeMtnMomoAppointmentPayment({ clinician_user_id: mtnClinicianUserId, service_id: mtnServiceId, starts_at: mtnStartsAt, idempotency_key: crypto.randomUUID(), payer_msisdn: payerMsisdn, consultation_type: consultationType, care_subject_type: careSubject?.type === "dependant" ? "dependant" : "self", care_subject_dependant_id: careSubject?.dependantId || "" }); if (result?.state === "paid" && result?.appointment_id) { await completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentLabel: "MTN MoMo" }); return; } const next = String(result?.state || "pending").toLowerCase(); setMtnMomoState({ phase: mtnPhase(next), attemptId: String(result?.attempt_id || ""), starting: false, checking: false, message: mtnMessage(next) }); } catch (error) { if (String(error?.code || "").toLowerCase() === "timeout" || String(error?.message || "").toLowerCase().includes("timed out")) { void recoverMtnMomoAfterTransportTimeout(); return; } setMtnMomoState((v) => ({ ...v, starting: false })); setSubmitError(error?.message || "MTN MoMo could not be started. No appointment has been confirmed."); } }
  React.useEffect(() => { if (paymentMethodId !== "mtn_momo" || !mtnClinicianUserId || !mtnServiceId || !mtnStartsAt) return undefined; let active = true; void recoverMtnMomoAppointmentPayment({ clinician_user_id: mtnClinicianUserId, service_id: mtnServiceId, starts_at: mtnStartsAt }).then((result) => { if (!active || !result?.attempt_id) return; if (result?.state === "paid" && result?.appointment_id) { void completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentLabel: "MTN MoMo" }); return; } const next = String(result?.state || "pending").toLowerCase(); setMtnMomoState({ phase: mtnPhase(next), attemptId: String(result.attempt_id), starting: false, checking: false, message: mtnMessage(next) }); }).catch(() => {}); return () => { active = false; }; }, [completeVerifiedAppointmentJourney, mtnClinicianUserId, mtnServiceId, mtnStartsAt, paymentMethodId]);
  React.useEffect(() => { if (paymentMethodId !== "mtn_momo" || mtnMomoState.phase !== "pending" || !mtnMomoState.attemptId) return undefined; const timer = window.setInterval(() => { void checkMtnMomoStatus(mtnMomoState.attemptId); }, 4_000); return () => window.clearInterval(timer); }, [mtnMomoState.attemptId, mtnMomoState.phase, paymentMethodId]);
  async function handleStartPaystackAppointmentPayment(capitecIdentifier = undefined) {
    if (paystackAppointmentState.starting || submitting) return;
    const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
    const serviceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
    if (!startsAt || !serviceId || !bookingDoctorId) {
      setSubmitError(t("appointments.checkout.errors.bookingIncompleteRetry", "Your booking details are incomplete. Please return and choose a time again."));
      return;
    }
    try {
      setSubmitError("");
      setPaystackAppointmentState({ starting: true, message: "" });
      const requestedPaymentMethod = String(paymentMethodId || "card").trim().toLowerCase() || "card";
      if (paystackAppointmentIdempotencyRef.current.method !== requestedPaymentMethod || !paystackAppointmentIdempotencyRef.current.key) {
        const storageKey = `afrimed:paystack-appointment:${booking?.bookingKey || `${bookingDoctorId}:${serviceId}:${startsAt}`}:${requestedPaymentMethod}`;
        const stored = window.sessionStorage.getItem(storageKey);
        const key = stored || crypto.randomUUID();
        paystackAppointmentIdempotencyRef.current = { method: requestedPaymentMethod, key };
        window.sessionStorage.setItem(storageKey, key);
      }
      const result = await initializePaystackAppointmentPayment({
        locale: languageCode,
        clinician_user_id: bookingDoctorId,
        service_id: serviceId,
        starts_at: startsAt,
        idempotency_key: paystackAppointmentIdempotencyRef.current.key,
        consultation_type: consultationType,
        practice_id: booking?.facilityId || booking?.facility?.id || doctor?.facility_id || doctor?.practice_id || "",
        service_name: serviceName,
        care_subject_type: careSubject?.type === "dependant" ? "dependant" : "self",
        care_subject_dependant_id: careSubject?.dependantId || "",
        payment_method: requestedPaymentMethod,
        capitec_identifier: requestedPaymentMethod === "capitec_pay" ? capitecIdentifier : undefined,
      });
      if (result?.appointment_id && ["payment_action_due", "authorised", "checkout_ready"].includes(result.state)) {
        if (result.authorization_url) window.location.assign(result.authorization_url);
        else nav(`/appointments/details/${result.appointment_id}`);
        setPaystackAppointmentState({ starting: false, message: "" });
        return;
      }
      if (result?.already_paid) {
        setPaystackAppointmentState({ starting: false, message: "This appointment payment is already being finalised." });
        return;
      }
      const authorizationUrl = String(result?.authorization_url || "").trim();
      if (result?.execution === "qr") {
        const attemptId = String(result?.attempt_id || "").trim();
        const reference = String(result?.reference || "").trim();
        const qrCode = String(result?.qr_code || "").trim();
        const imageUrl = String(result?.qr_image_url || "").trim();
        if (!attemptId || !reference || !qrCode || !imageUrl) {
          throw new Error("Scan to Pay could not be prepared. No appointment has been confirmed.");
        }
        setScanToPayState({
          attemptId,
          reference,
          qrCode,
          imageUrl,
          state: "pending",
          checking: false,
          message: "Waiting for secure payment confirmation. Your appointment is not confirmed until payment is verified.",
        });
        setPaystackAppointmentState({ starting: false, message: "" });
        return;
      }
      if (result?.execution === "capitec_pay") {
        const attemptId = String(result?.attempt_id || "").trim();
        const reference = String(result?.reference || "").trim();
        if (!attemptId || !reference) throw new Error("Capitec Pay could not be prepared. No appointment has been confirmed.");
        setScanToPayState({ attemptId, reference, method: "capitec_pay", state: "pending", checking: false, message: "" });
        setPaystackAppointmentState({ starting: false, message: "" });
        return;
      }
      if (!authorizationUrl) throw new Error("Secure payment could not be prepared.");
      const handoffStorageKey = `afrimed:paystack-appointment-handoff:${booking?.bookingKey || `${bookingDoctorId}:${serviceId}:${startsAt}`}`;
      window.sessionStorage.setItem(handoffStorageKey, JSON.stringify({
        attemptId: String(result?.attempt_id || "").trim(),
        reference: String(result?.reference || "").trim(),
      }));
      paystackExternalHandoffRef.current = true;
      checkoutClickedRef.current = true;
      logPaymentEvent("paystack_appointment_checkout_started", { attempt_id: result?.attempt_id || null });
      window.location.assign(authorizationUrl);
    } catch (error) {
      setPaystackAppointmentState({ starting: false, message: "" });
      setSubmitError(t("appointments.checkout.errors.paymentFailed", "Payment could not be completed."));
    }
  }

  const reconcileExternalPaystackHandoff = React.useCallback(async () => {
    const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
    const serviceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
    if (!startsAt || !serviceId || !bookingDoctorId) {
      setPaystackAppointmentState({ starting: false, message: "" });
      return;
    }
    const handoffStorageKey = `afrimed:paystack-appointment-handoff:${booking?.bookingKey || `${bookingDoctorId}:${serviceId}:${startsAt}`}`;
    const handoff = safeParse(window.sessionStorage.getItem(handoffStorageKey) || "");
    const attemptId = String(handoff?.attemptId || "").trim();
    if (!attemptId) {
      if (paystackExternalHandoffRef.current) {
        setPaystackAppointmentState({ starting: false, message: "Secure payment was not started. You can continue when ready." });
      }
      return;
    }
    setPaystackAppointmentState({ starting: false, message: "Checking secure payment status…" });
    try {
      const result = await verifyPaystackAppointmentPayment({ attempt_id: attemptId });
      if (result?.state === "paid" && result?.appointment_id) {
        window.sessionStorage.removeItem(handoffStorageKey);
        await completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentIntentId: result?.reference || "" });
        return;
      }
      if (result?.state === "pending") {
        setPaystackAppointmentState({ starting: false, message: "Payment is still pending. Your appointment is not confirmed until payment is verified." });
        return;
      }
      window.sessionStorage.removeItem(handoffStorageKey);
      setPaystackAppointmentState({ starting: false, message: "Payment was not completed. You can try the selected payment method again." });
    } catch {
      setPaystackAppointmentState({ starting: false, message: "We couldn’t verify payment yet. Your appointment is not confirmed." });
    }
  }, [booking?.bookingKey, booking?.selectedDate, booking?.selectedServiceId, booking?.selectedTime, booking?.serviceId, bookingDoctorId, completeVerifiedAppointmentJourney]);

  React.useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === "visible") void reconcileExternalPaystackHandoff();
    };
    window.addEventListener("pageshow", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("pageshow", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [reconcileExternalPaystackHandoff]);

  const checkScanToPayStatus = React.useCallback(async () => {
    const attemptId = String(scanToPayState?.attemptId || "").trim();
    if (!attemptId || scanToPayVerificationRef.current) return;
    scanToPayVerificationRef.current = true;
    setScanToPayState((current) => current ? { ...current, checking: true, message: "Checking secure payment status…" } : current);
    try {
      const result = await verifyPaystackAppointmentPayment({ attempt_id: attemptId });
      if (result?.state === "paid" && result?.appointment_id) {
        await completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentIntentId: result?.reference || "" });
        return;
      }
      const failed = result?.state === "failed";
      setScanToPayState((current) => current ? {
        ...current,
        checking: false,
        state: failed ? "failed" : "pending",
        message: failed
          ? "Payment was not completed. No appointment has been confirmed."
          : "Payment is still pending. Your appointment is not confirmed until payment is verified.",
      } : current);
    } catch {
      setScanToPayState((current) => current ? {
        ...current,
        checking: false,
        message: "We couldn’t verify payment yet. Your appointment is not confirmed.",
      } : current);
    } finally {
      scanToPayVerificationRef.current = false;
    }
  }, [completeVerifiedAppointmentJourney, scanToPayState?.attemptId]);

  React.useEffect(() => {
    if (!scanToPayState?.attemptId || scanToPayState.state === "failed") return undefined;
    const timeout = window.setTimeout(() => { void checkScanToPayStatus(); }, 10_000);
    return () => window.clearTimeout(timeout);
  }, [checkScanToPayStatus, scanToPayState?.attemptId, scanToPayState?.state]);

  async function handlePaymentTestHarness() {
    if (!isPaystackTestHelper || paymentTestState.running) return;
    setPaymentTestState({ running: true, message: "" });
    try {
      const callback = new URL(window.location.href);
      callback.searchParams.set("paystack_test_harness", "1");
      const result = await initializePaystackPaymentHarness({
        customer_email: String(auth?.user?.email || "").trim(),
        callback_url: callback.toString(),
      });
      const accessCode = String(result?.accessCode || "").trim();
      if (!accessCode) throw new Error("Secure Test payment could not be prepared.");
      await openPaystackInlineCheckout(accessCode);
      setPaymentTestState({ running: false, message: "Secure Test payment is open. Complete it in the secure window." });
      const reference = String(result?.reference || "").trim();
      if (!reference) return;
      void (async () => {
        for (let attempt = 0; attempt < 45; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 2000));
          try {
            const verified = await verifyPaystackPaymentHarness({ reference });
            if (verified?.ok) {
              setPaymentTestState({ running: false, message: "Secure Test payment verified. No appointment was created." });
              return;
            }
          } catch (verifyError) {
            if (attempt === 44) {
              if (import.meta.env.DEV) console.error("appointment_payment_test_harness_popup_verify_failed", verifyError);
              setPaymentTestState({ running: false, message: "We couldn’t verify the secure Test payment." });
            }
          }
        }
      })();
    } catch (error) {
      if (import.meta.env.DEV) console.error("appointment_payment_test_harness_failed", error);
      setPaymentTestState({ running: false, message: "We couldn’t start the secure Test payment. Please try again." });
    }
  }

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    // Paystack hosted checkout returns its merchant identity as `trxref`.
    // Accept the standard `reference` spelling too, then let the authenticated
    // server resolve and verify the durable payment attempt.
    const reference = String(params.get("reference") || params.get("trxref") || "").trim();
    if (params.get("paystack_appointment") !== "1" || !reference) return undefined;
    let active = true;
    setPaystackAppointmentState({ starting: true, message: "Verifying secure payment…" });
    void verifyPaystackAppointmentPayment({ reference })
      .then((result) => {
        if (!active) return;
        if (result?.state === "paid" && result?.appointment_id) {
          setPaystackAppointmentState({ starting: false, message: "Payment verified. Your appointment request is confirmed." });
          void completeVerifiedAppointmentJourney({ appointmentId: result.appointment_id, paymentIntentId: result?.reference || "" });
          return;
        }
        setPaystackAppointmentState({ starting: false, message: "Payment is not complete yet. No appointment has been confirmed." });
      })
      .catch(() => {
        if (active) setPaystackAppointmentState({ starting: false, message: "We couldn’t verify payment yet. No appointment has been confirmed." });
      });
    return () => { active = false; };
  }, [completeVerifiedAppointmentJourney, location.search]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const reference = String(params.get("reference") || "").trim();
    if (!isPaystackTestHelper || params.get("paystack_test_harness") !== "1" || !reference) return undefined;
    let active = true;
    setPaymentTestState({ running: true, message: "Verifying secure Test payment…" });
    void verifyPaystackPaymentHarness({ reference })
      .then((result) => {
        if (!active) return;
        setPaymentTestState({ running: false, message: result?.ok ? "Secure Test payment verified. No appointment was created." : "Secure Test payment was not completed." });
      })
      .catch((error) => {
        if (!active) return;
        if (import.meta.env.DEV) console.error("appointment_payment_test_harness_verify_failed", error);
        setPaymentTestState({ running: false, message: "We couldn’t verify the secure Test payment." });
      });
    return () => { active = false; };
  }, [isPaystackTestHelper, location.search]);
  const sponsorRecoveryHint = React.useMemo(
    () => ({
      ...(pendingSponsorRecovery?.recoveryHint || {}),
      source: "appointment_checkout",
      doctor_user_id: bookingDoctorId,
      patient_user_id: auth?.user?.id || "",
      guardian_user_id: auth?.user?.id || "",
      doctor_name: doctorDisplayName,
      patient_name: patientName,
      guardian_name: guardianName,
      care_subject_type: careSubject?.type || "self",
      care_subject_dependant_id: careSubject?.dependantId || "",
      care_subject_relationship: careSubject?.relationship || "",
      service_id: booking?.serviceId || booking?.selectedServiceId || "",
      service_name: serviceName,
      selected_date: booking?.selectedDate || "",
      selected_time: booking?.selectedTime || "",
      duration_minutes: bookingDurationMinutes,
      consultation_type: consultationType,
      practice_id:
        booking?.facilityId ||
        booking?.facility?.id ||
        doctor?.facility_id ||
        doctor?.practice_id ||
        "",
      facility_id: booking?.facilityId || booking?.facility?.id || "",
      facility_name: booking?.facilityName || "",
      mode: booking?.mode || "",
      booking_key: booking?.bookingKey || bookingKeyParam,
    }),
    [
      auth?.user?.id,
      booking?.bookingKey,
      booking?.facility?.id,
      booking?.facilityId,
      booking?.facilityName,
      booking?.mode,
      booking?.selectedDate,
      booking?.selectedServiceId,
      booking?.selectedTime,
      booking?.serviceId,
      bookingKeyParam,
      bookingDoctorId,
      bookingDurationMinutes,
      careSubject?.dependantId,
      careSubject?.relationship,
      careSubject?.type,
      consultationType,
      doctor?.facility_id,
      doctor?.practice_id,
      doctorDisplayName,
      guardianName,
      pendingSponsorRecovery?.recoveryHint,
      patientName,
      serviceName,
    ],
  );

  React.useEffect(() => {
    setCareSubjectConfirmed(!careSubjectConfirmationRequired);
  }, [careSubjectConfirmationRequired, patientName]);

  React.useEffect(() => {
    try {
      localStorage.setItem(REMEMBER_CARD_STORAGE_KEY, rememberCard ? "true" : "false");
    } catch {
      // ignore storage failures
    }
  }, [rememberCard]);

  React.useEffect(() => {
    let alive = true;

    async function loadDoctorTerms() {
      if (!bookingDoctorId) {
        if (alive) {
          setDoctorTermsState({
            doctorTermsUrl: "",
            doctorTermsUpdatedAt: "",
          });
        }
        return;
      }

      try {
        const result = await loadDoctorTermsDisclosure(bookingDoctorId);
        if (!alive) return;
        setDoctorTermsState({
          doctorTermsUrl: String(result?.doctorTermsUrl || "").trim(),
          doctorTermsUpdatedAt: String(result?.doctorTermsUpdatedAt || "").trim(),
        });
      } catch {
        if (!alive) return;
        setDoctorTermsState({
          doctorTermsUrl: "",
          doctorTermsUpdatedAt: "",
        });
      }
    }

    void loadDoctorTerms();

    return () => {
      alive = false;
    };
  }, [bookingDoctorId]);

  React.useEffect(() => {
    async function loadSavedCards() {
      // Saved card records belong to the retired provider contract. Hosted
      // Paystack and explicit Worldline checkouts collect instruments safely
      // in their own secure boundary.
      setSavedCardsState({ loading: false, error: "", customerId: "", methods: [] });
      setSelectedSavedPaymentMethodId("");
      setUseSavedPaymentMethod(false);
    }

    void loadSavedCards();
  }, [
    guardianEmail,
    isPaystackTest,
    requiresCardCheckout,
    checkoutRefreshKey,
    evaluationAccessState.loading,
    evaluationAccessState.available,
  ]);

  React.useEffect(() => {
    let alive = true;

    async function loadMethods() {
      if (!bookingDoctorId) {
        if (alive) setMethodsState({ loading: false, error: "", methods: [] });
        return;
      }

      setMethodsState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await loadDoctorPaymentMethods(bookingDoctorId, supportedProviderMethods);
        const clinicianMethods = Array.isArray(result.methods) ? result.methods : [];
        const patientMethods = filterAppointmentPaystackMethodsForPatient(clinicianMethods);
        recordAppointmentPaymentCapabilityDiagnostics({
          providerMethods: supportedProviderMethods,
          clinicianMeta: result.meta,
          clinicianEnabledMethods: clinicianMethods,
          patientMethods,
          capabilitySource: "appointment_checkout",
        });
        if (!alive) return;
        setMethodsState({
          loading: false,
          error: "",
          methods: patientMethods,
        });
      } catch (error) {
        if (!alive) return;
        setMethodsState({
          loading: false,
          error:
            error?.message ||
            t("appointments.book.errors.loadPayments", "Unable to load payment methods."),
          methods: [],
        });
      }
    }

    loadMethods();
    return () => {
      alive = false;
    };
  }, [bookingDoctorId, supportedProviderMethods, t]);

  React.useEffect(() => {
    let alive = true;

    function resetIntentState() {
      if (evaluationAccessState.loading || evaluationAccessState.available || isSponsorReturn) {
        if (alive) {
          setIntentState((prev) => ({
            ...prev,
            loading: false,
            error: "",
            clientSecret: "",
            paymentIntentId: "",
            platformAccountId: "",
            publishableKey: "",
            livemode: null,
            customerReused: false,
          }));
          setStripeState({ loading: false, error: "" });
        }
        return;
      }

      if (!requiresCardCheckout || !booking || !bookingDoctorId) {
        if (alive) {
          setIntentState((prev) => ({
            ...prev,
            loading: false,
            error: requiresCardCheckout
              ? t(
                  "appointments.checkout.errors.bookingIncomplete",
                  "Booking details are incomplete."
                )
              : "",
            clientSecret: "",
            paymentIntentId: "",
            platformAccountId: "",
            publishableKey: "",
            livemode: null,
            applicationFeeAmount: 0,
            doctorNetAmount: 0,
            customerReused: false,
          }));
        }
        return;
      }

      if (alive) {
        setIntentState((prev) => ({
          ...prev,
          loading: false,
          error: "",
          clientSecret: "",
          paymentIntentId: "",
          platformAccountId: "",
          publishableKey: "",
          livemode: null,
          applicationFeeAmount: 0,
          doctorNetAmount: 0,
          currency: currency || "ZAR",
          customerReused: false,
        }));
      }
    }

    resetIntentState();
    return () => {
      alive = false;
    };
  }, [
    booking,
    bookingDoctorId,
    checkoutRefreshKey,
    currency,
    requiresCardCheckout,
    isSponsorReturn,
    evaluationAccessState.loading,
    evaluationAccessState.available,
  ]);

  const stagingStripeModeMismatch =
    String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase() === "staging" &&
    intentState.livemode === true;
  const worldlineSandboxEnabled = isWorldlineSandboxEntryEnabled();
  React.useEffect(() => {
    let active = true;
    // Evaluation access is a non-provider financial path. Do not probe or
    // expose provider sandbox controls while its entitlement is resolving or
    // available.
    if (
      !worldlineSandboxEnabled ||
      !auth?.user?.id ||
      evaluationAccessState.loading ||
      evaluationAccessState.available
    ) {
      setWorldlineEligible(false);
      return () => { active = false; };
    }
    void getWorldlineSandboxAvailability().then((eligible) => {
      if (active) setWorldlineEligible(eligible);
    });
    return () => { active = false; };
  }, [
    auth?.user?.id,
    worldlineSandboxEnabled,
    evaluationAccessState.loading,
    evaluationAccessState.available,
  ]);

  const selectedPaymentMethod = React.useMemo(
    () =>
      methodsState.methods.find((method) => method.id === paymentMethodId) || null,
    [methodsState.methods, paymentMethodId],
  );
  const paymentLabel = localizePaymentMethodLabel(
    booking?.paymentMethod || selectedPaymentMethod?.id || paymentMethodId,
    t,
    booking?.paymentMethodLabel ||
      selectedPaymentMethod?.label ||
      t("appointments.checkout.fields.paymentMethod", "Payment method"),
  );
  const insuranceContextLabel = resolveInsuranceContextLabel(booking, t);
  const appointmentLabel =
    booking?.selectedDate && booking?.selectedTime
      ? `${formatDate(booking.selectedDate, {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        })} · ${booking.selectedTime}`
      : "";
  const checkoutSteps = React.useMemo(
    () => [
      t("appointments.checkout.steps.appointment", "Appointment"),
      t("appointments.checkout.steps.reviewAndBook", "Review & book"),
      t("appointments.checkout.steps.payment", "Payment"),
    ],
    [t],
  );

  // Hosted payment providers own the secure card surface. The retired
  // browser-SDK branch is deliberately never resolved from this checkout.
  const stripePromise = React.useMemo(() => Promise.resolve(null), []);

  React.useEffect(() => {
    let alive = true;

    async function resolveStripe() {
      if (
        !requiresCardCheckout ||
        evaluationAccessState.loading ||
        evaluationAccessState.available
      ) {
        if (alive) {
          setStripeState({ loading: false, error: "" });
        }
        return;
      }

      setStripeState({ loading: true, error: "" });
      try {
        const stripe = await stripePromise;
        if (!alive) return;
        if (!stripe) {
          setStripeState({
            loading: false,
            error: t(
              "appointments.checkout.errors.stripeNotConfigured",
              "Secure card checkout is not configured yet on this environment."
            ),
          });
          return;
        }
        setStripeState({ loading: false, error: "" });
      } catch (error) {
        if (!alive) return;
        setStripeState({
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.checkout.errors.stripeLoad",
              "Secure card checkout could not load right now."
            ),
        });
      }
    }

    resolveStripe();
    return () => {
      alive = false;
    };
  }, [
    requiresCardCheckout,
    stripePromise,
    checkoutRefreshKey,
    evaluationAccessState.loading,
    evaluationAccessState.available,
  ]);

  function handleRefreshCheckout() {
    setSubmitError("");
    setIntentState((prev) => ({
      ...prev,
      loading: false,
      error: "",
      clientSecret: "",
      paymentIntentId: "",
      platformAccountId: "",
      publishableKey: "",
      livemode: null,
      applicationFeeAmount: 0,
      doctorNetAmount: 0,
      currency: currency || "ZAR",
      customerReused: false,
    }));
    setCheckoutRefreshKey((current) => current + 1);
  }

  const handleSponsorSyncSuccess = React.useCallback(
    async (syncResult = {}) => {
      const appointmentId = String(syncResult?.appointment_id || "").trim();
      if (!appointmentId) {
        throw new Error(
          t(
            "appointments.checkout.errors.sponsorRestore",
            "Sponsor payment completed, but the appointment could not be restored."
          )
        );
      }

      const syncedBooking = syncResult?.booking || {};
      const nextAmountPaid =
        Number(syncResult?.amount_cents || 0) > 0 ? Number(syncResult.amount_cents) / 100 : amountPaid;
      const nextCurrency = String(syncResult?.currency || currency || "ZAR").trim() || "ZAR";
      const nextSelectedDate = String(syncedBooking?.selected_date || booking?.selectedDate || "").trim();
      const nextSelectedTime = String(syncedBooking?.selected_time || booking?.selectedTime || "").trim();
      const nextDurationMinutes = normalizeDurationMinutes(
        syncedBooking?.duration_minutes ?? bookingDurationMinutes,
        30,
      );

      const appointmentReminderPreference = await persistPatientAppointmentReminderDefault(
        auth?.user?.id,
      );
      const successState = {
        doctor,
        appointmentId,
        amountPaid: nextAmountPaid,
        currency: nextCurrency,
        careSubjectType: careSubject?.type || "self",
        careSubjectId: careSubject?.dependantId || careSubject?.selectionId || null,
        careSubjectName: patientName,
        careSubjectRelationship: careSubject?.relationship || "",
        careSubjectDateOfBirth: careSubject?.dateOfBirth || null,
        guardianUserId: auth?.user?.id || null,
        guardianName,
        patientName,
        patient_name: patientName,
        patient: {
          name: patientName,
          guardian_name: guardianName,
          care_subject_type: careSubject?.type || "self",
          care_subject_relationship: careSubject?.relationship || "",
          responsible_relationship: careSubject?.relationship || "",
        },
        paymentLabel: t("appointments.checkout.steps.sponsorPayment", "Sponsor payment"),
        insurancePaymentMode: booking?.insurancePaymentMode || "self_pay",
        insuranceProvider: booking?.insuranceProvider || "",
        insuranceContextLabel,
        insuranceProviderOptions: booking?.insuranceProviderOptions || [],
        insuranceAcceptedLabel: booking?.insuranceAcceptedLabel || "",
        paymentIntentId: String(syncResult?.payment_intent_id || "").trim(),
        serviceId:
          String(
            syncedBooking?.service_id || booking?.serviceId || booking?.selectedServiceId || "",
          ).trim() || null,
        serviceName: String(
          syncedBooking?.service_name ||
            serviceName ||
            t("appointments.confirm.serviceFallback", "Consultation")
        ).trim(),
        serviceNamePrice: formatAmount(nextAmountPaid, nextCurrency, languageCode),
        selectedDate: nextSelectedDate,
        selectedTime: nextSelectedTime,
        mode: String(syncedBooking?.mode || booking?.mode || "").trim() || null,
        facilityId:
          String(
            syncedBooking?.facility_id ||
              booking?.facilityId ||
              booking?.facility?.id ||
              doctor?.facility_id ||
              doctor?.practice_id ||
              "",
          ).trim() || null,
        facilityName: String(syncedBooking?.facility_name || booking?.facilityName || "").trim(),
        facility: booking?.facility || null,
        consultation_type:
          String(syncedBooking?.consultation_type || consultationType || "private").trim(),
        practice_id:
          String(
            syncedBooking?.practice_id ||
              booking?.facilityId ||
              booking?.facility?.id ||
              doctor?.facility_id ||
              doctor?.practice_id ||
              "",
          ).trim() || null,
        when: {
          dateISO: nextSelectedDate,
          time: nextSelectedTime,
          duration_mins: nextDurationMinutes,
        },
        duration: nextDurationMinutes,
        durationMinutes: nextDurationMinutes,
        duration_minutes: nextDurationMinutes,
        appointmentRemindersDefaulted: appointmentReminderPreference.defaulted,
        appointmentReminderRevision: appointmentReminderPreference.updatedAt || null,
        appointmentStatus: String(syncResult?.appointment_status || "pending").trim().toLowerCase() || "pending",
      };

      persistSnapshots(appointmentId, successState);
      clearPendingSponsorPayment(String(auth?.user?.id || "").trim(), "appointment_checkout");
      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      window.dispatchEvent(new Event("afrimed:notifications:changed"));
      window.dispatchEvent(new Event("afrimed:transactions:changed"));
      nav("/appointments/success", {
        replace: true,
        state: successState,
      });
    },
    [
      amountPaid,
      auth?.user?.id,
      booking,
      bookingDurationMinutes,
      careSubject,
      consultationType,
      currency,
      doctor,
      guardianName,
      insuranceContextLabel,
      nav,
      patientName,
      serviceName,
    ],
  );

  React.useEffect(() => {
    let alive = true;

    async function syncSponsorReturn() {
      const paymentLinkId = String(pendingSponsorRecovery?.paymentLinkId || "").trim();
      if (!isSponsorReturn || (!sponsorSessionId && !paymentLinkId)) {
        if (alive) setSponsorReturnState({ loading: false, error: "" });
        return;
      }

      setSponsorReturnState({ loading: true, error: "" });

      try {
        const syncPayload = sponsorSessionId
          ? { session_id: sponsorSessionId, recovery_hint: sponsorRecoveryHint }
          : { payment_link_id: paymentLinkId, recovery_hint: sponsorRecoveryHint };
        const syncResult = await syncSponsorPaymentSession(syncPayload);
        if (!alive) return;
        await handleSponsorSyncSuccess(syncResult);
      } catch (error) {
        if (!alive) return;
        setSponsorReturnState({
          loading: false,
          error:
            error?.message ||
            t(
              "appointments.checkout.errors.sponsorSync",
              "Sponsor payment completed, but AfriMed™ could not restore the appointment yet."
            ),
        });
      }
    }

    void syncSponsorReturn();

    return () => {
      alive = false;
    };
  }, [
    auth?.user?.id,
    amountPaid,
    booking,
    bookingDurationMinutes,
    consultationType,
    currency,
    doctor,
    handleSponsorSyncSuccess,
    isSponsorReturn,
    nav,
    pendingSponsorRecovery?.paymentLinkId,
    sponsorRecoveryHint,
    serviceName,
    sponsorSessionId,
    checkoutRefreshKey,
  ]);

  const bookingRouteState = React.useMemo(
    () => ({
      ...(booking || {}),
      bookingKey: booking?.bookingKey || bookingKeyParam || null,
      doctor: doctor || booking?.doctor || null,
      doctorId: bookingDoctorId,
    }),
    [booking, bookingDoctorId, bookingKeyParam, doctor],
  );

  function handleBackToCheckout() {
    if (!bookingDoctorId) {
      nav(-1);
      return;
    }

    nav(`/appointments/checkout/${bookingDoctorId}`, {
      state: bookingRouteState,
    });
  }

  function handleChooseDifferentMethod() {
    clearPendingSponsorPayment(String(auth?.user?.id || "").trim(), "appointment_checkout");
    if (!bookingDoctorId) {
      nav(-1);
      return;
    }

    nav(`/appointments/book/${bookingDoctorId}`, {
      state: bookingRouteState,
    });
  }

  function handleContinueWithCardPayment() {
    clearPendingSponsorPayment(String(auth?.user?.id || "").trim(), "appointment_checkout");
    setSponsorReturnState({ loading: false, error: "" });
    setSubmitError("");
    const bookingKeyQuery = booking?.bookingKey || bookingKeyParam
      ? `?booking_key=${encodeURIComponent(booking?.bookingKey || bookingKeyParam)}`
      : "";

    if (!bookingDoctorId) {
      setCheckoutRefreshKey((current) => current + 1);
      return;
    }

    nav(`/appointments/payment/${bookingDoctorId}${bookingKeyQuery}`, {
      replace: true,
      state: bookingRouteState,
    });
    setCheckoutRefreshKey((current) => current + 1);
  }

  function handlePrepareSponsorPayment() {
    if (careSubjectConfirmationRequired && !careSubjectConfirmed) {
      setSubmitError(
        t(
          "appointments.checkout.errors.confirmCareSubjectBeforeProceed",
          "Please confirm this appointment is for {name} before proceeding.",
          { name: patientName || t("appointments.checkout.patientFallback", "Patient") },
        ),
      );
      return;
    }

    if (!bookingDoctorId) {
      nav("/settings/patient/preferences/transactions/prepare-sponsor-payment");
      return;
    }

    if (worldlineSandboxEnabled && worldlineEligible) {
      setWorldlineSponsorLink((current) => ({ ...current, error: "" }));
      setWorldlineSponsorDialogOpen(true);
      return;
    }

    nav("/settings/patient/preferences/transactions/prepare-sponsor-payment", {
      state: {
        source: "appointment_checkout",
        returnTo: `/appointments/payment/${bookingDoctorId}`,
        returnState: bookingRouteState,
        prefill: {
          paymentFor: serviceName,
          amount: amountPaid > 0 ? amountPaid.toFixed(2) : "",
          currency,
        },
        doctorUserId: bookingDoctorId,
        doctorName: doctorDisplayName,
        doctorStripeAccountId: String(doctor?.stripe_account_id || "").trim(),
        successUrl: `${window.location.origin}/appointments/payment/${bookingDoctorId}?sponsor_payment=1&session_id={CHECKOUT_SESSION_ID}${booking?.bookingKey || bookingKeyParam ? `&booking_key=${encodeURIComponent(booking?.bookingKey || bookingKeyParam)}` : ""}`,
        cancelUrl: `${window.location.origin}/appointments/payment/${bookingDoctorId}${booking?.bookingKey || bookingKeyParam ? `?booking_key=${encodeURIComponent(booking?.bookingKey || bookingKeyParam)}` : ""}`,
        metadata: sponsorRecoveryHint,
      },
    });
  }

  async function handleCreateWorldlineSponsorLink() {
    const serviceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
    const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
    if (!bookingDoctorId || !serviceId || !startsAt) {
      setWorldlineSponsorLink((current) => ({ ...current, error: t("worldline.sponsor.bookingIncomplete", "Booking details are incomplete for sponsor payment.") }));
      return;
    }
    setWorldlineSponsorLink((current) => ({ ...current, creating: true, error: "" }));
    try {
      const result = await createWorldlineSponsorLink({ clinicianUserId: bookingDoctorId, serviceId, startsAt });
      const expiresAt = String(result?.expires_at || "").trim();
      setWorldlineSponsorLink({
        url: String(result?.url || "").trim(),
        sponsorLinkId: String(result?.sponsor_link_id || "").trim(),
        expiresLabel: expiresAt ? formatDate(expiresAt, { dateStyle: "medium", timeStyle: "short" }) : "",
        creating: false,
        revoking: false,
        error: "",
      });
    } catch (error) {
      setWorldlineSponsorLink((current) => ({ ...current, creating: false, error: error?.message || t("worldline.sponsor.createUnavailable", "Secure sponsor link could not be created.") }));
    }
  }

  async function handleRevokeWorldlineSponsorLink() {
    const sponsorLinkId = String(worldlineSponsorLink.sponsorLinkId || "").trim();
    if (!sponsorLinkId) return;
    setWorldlineSponsorLink((current) => ({ ...current, revoking: true, error: "" }));
    try {
      await revokeWorldlineSponsorLink(sponsorLinkId);
      setWorldlineSponsorLink({ url: "", sponsorLinkId: "", expiresLabel: "", creating: false, revoking: false, error: "" });
    } catch (error) {
      setWorldlineSponsorLink((current) => ({ ...current, revoking: false, error: error?.message || t("worldline.sponsor.revokeUnavailable", "Secure sponsor link could not be revoked.") }));
    }
  }

  async function handleEvaluationAccessBooking() {
    if (careSubjectConfirmationRequired && !careSubjectConfirmed) {
      setSubmitError(
        t(
          "appointments.checkout.errors.confirmCareSubjectBeforeProceed",
          "Please confirm this appointment is for {name} before proceeding.",
          { name: patientName || t("appointments.checkout.patientFallback", "Patient") },
        ),
      );
      return;
    }
    if (submitting || !evaluationAccessState.available) return;

    const serviceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
    const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
    if (!bookingDoctorId || !serviceId || !startsAt) {
      setSubmitError(t("appointments.checkout.errors.bookingIncompleteRetry", "Booking details are incomplete. Refresh payment details and try again."));
      return;
    }

    checkoutClickedRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      if (!evaluationIdempotencyRef.current) evaluationIdempotencyRef.current = crypto.randomUUID();
      const result = await redeemEvaluationAccessBooking({
        doctorUserId: bookingDoctorId,
        serviceId,
        startsAt,
        durationMinutes: bookingDurationMinutes,
        practiceId: booking?.facilityId || booking?.facility?.id || doctor?.facility_id || doctor?.practice_id || null,
        careSubject,
        idempotencyKey: evaluationIdempotencyRef.current,
      });
      const appointmentId = String(result?.appointmentId || "").trim();
      if (!appointmentId) throw new Error("evaluation_booking_failed");

      paymentCompletedRef.current = true;
      logPaymentEvent("evaluation_sponsored_appointment_created", {
        appointment_id: appointmentId,
        payment_method: "evaluation_sponsorship",
        redemption_id: String(result?.redemptionId || "").trim() || null,
      });

      // The Evaluation server operation creates the clinician notification
      // with service authority. Do not depend on a cross-user browser write
      // here: an Evaluation booking must use the ordinary pending-approval
      // notification path.
      await createAppointmentNotification({
        userId: String(auth?.user?.id || "").trim(),
        title: t("appointments.checkout.notifications.patientAppointmentRequestedTitle", "Appointment requested"),
        message: t("appointments.checkout.notifications.patientAppointmentRequested", "Your appointment request for {datetime} was sent.", {
          datetime: formatNotificationAppointmentDate(startsAt, languageCode),
        }),
        type: `appointment_booking_request:${appointmentId}:patient`,
        priority: "urgent",
      }).catch(() => {});
      await sendAppointmentEventEmail({ appointmentId, eventType: "booking_created" }).catch(() => {});
      const appointmentReminderPreference = await persistPatientAppointmentReminderDefault(auth?.user?.id);
      const successState = {
        doctor, appointmentId, amountPaid, currency,
        careSubjectType: careSubject?.type || "self", careSubjectId: careSubject?.dependantId || careSubject?.selectionId || null,
        careSubjectName: patientName, careSubjectRelationship: careSubject?.relationship || "", careSubjectDateOfBirth: careSubject?.dateOfBirth || null,
        guardianUserId: auth?.user?.id || null, guardianName, patientName, patient_name: patientName,
        patient: { name: patientName, guardian_name: guardianName, care_subject_type: careSubject?.type || "self", care_subject_relationship: careSubject?.relationship || "", responsible_relationship: careSubject?.relationship || "" },
        paymentLabel: t("appointments.checkout.evaluationAccess", "Evaluation access"),
        paymentMethod: "evaluation_sponsorship", paymentStatus: "evaluation_sponsored", settlementRequired: false,
        insurancePaymentMode: booking?.insurancePaymentMode || "self_pay", insuranceProvider: booking?.insuranceProvider || "", insuranceContextLabel: t("appointments.checkout.evaluationSponsorship", "Evaluation sponsorship"),
        serviceId, serviceName, serviceNamePrice: formatAmount(amountPaid, currency, languageCode), selectedDate: booking?.selectedDate, selectedTime: booking?.selectedTime,
        mode: booking?.mode || null, facilityId: booking?.facilityId || null, facilityName: booking?.facilityName || "", facility: booking?.facility || null,
        consultation_type: consultationType, practice_id: booking?.facilityId || booking?.facility?.id || doctor?.facility_id || doctor?.practice_id || null,
        when: { dateISO: booking?.selectedDate, time: booking?.selectedTime, duration_mins: bookingDurationMinutes },
        duration: bookingDurationMinutes, durationMinutes: bookingDurationMinutes, duration_minutes: bookingDurationMinutes,
        appointmentRemindersDefaulted: appointmentReminderPreference.defaulted, appointmentReminderRevision: appointmentReminderPreference.updatedAt || null,
      };
      persistSnapshots(appointmentId, successState);
      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      window.dispatchEvent(new Event("afrimed:notifications:changed"));
      nav("/appointments/success", { state: successState });
    } catch (error) {
      logPaymentEvent("evaluation_sponsored_booking_failed", { error_category: categorizeCheckoutError(error) });
      setSubmitError(formatCheckoutError(error, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCardSubmit({ stripe, elements }) {
    if (careSubjectConfirmationRequired && !careSubjectConfirmed) {
      setSubmitError(
        t(
          "appointments.checkout.errors.confirmCareSubjectBeforeProceed",
          "Please confirm this appointment is for {name} before proceeding.",
          { name: patientName || t("appointments.checkout.patientFallback", "Patient") },
        ),
      );
      return;
    }

    if (submitting) return;
    checkoutClickedRef.current = true;
    logPaymentEvent("checkout_pay_clicked", {
      payment_method: paymentMethodId || "",
      saved_payment_method: useSavedPaymentMethod,
      care_subject_type: careSubject?.type || "self",
    });
    setSubmitting(true);
    setSubmitError("");

    try {
      const amountCents = Math.round(Number(amountPaid || 0) * 100);
      if (!amountCents || amountCents < 50) {
        throw new Error(
          t(
            "appointments.checkout.errors.missingAmount",
            "Consultation amount is missing for this booking."
          )
        );
      }

      const savedPaymentMethodId = useSavedPaymentMethod
        ? String(selectedSavedPaymentMethodId || "").trim()
        : "";
      const cardElement = savedPaymentMethodId ? null : elements.getElement(CardElement);
      if (!savedPaymentMethodId && !cardElement) {
        throw new Error(
          t(
            "appointments.checkout.errors.cardFormRefresh",
            "Secure card form is still loading. Please refresh payment details."
          )
        );
      }

      setIntentState((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      const nextIntent = await createAppointmentPaymentIntent({
        amountCents,
        currency,
        customerEmail: guardianEmail,
        doctorUserId: bookingDoctorId,
        serviceName,
        bookingDate: booking?.selectedDate,
        bookingTime: booking?.selectedTime,
        savePaymentMethod: rememberCard,
        metadata: {
          consultation_type: consultationType,
          booking_mode: String(booking?.mode || "").trim(),
          source: "appointment_checkout",
          doctor_name: doctorDisplayName,
          patient_name: patientName,
          guardian_name: guardianName,
          guardian_email: guardianEmail,
          care_subject_type: careSubject?.type || "self",
          care_subject_dependant_id: careSubject?.dependantId || "",
          care_subject_relationship: careSubject?.relationship || "",
          service_id: booking?.serviceId || booking?.selectedServiceId || "",
          selected_date: booking?.selectedDate || "",
          selected_time: booking?.selectedTime || "",
          duration_minutes: bookingDurationMinutes,
          practice_id:
            booking?.facilityId ||
            booking?.facility?.id ||
            doctor?.facility_id ||
            doctor?.practice_id ||
            "",
          facility_id: booking?.facilityId || booking?.facility?.id || "",
          facility_name: booking?.facilityName || "",
          insurance_payment_mode: booking?.insurancePaymentMode || "self_pay",
          insurance_provider: booking?.insuranceProvider || "",
          insurance_context_label: insuranceContextLabel,
          insurance_accepted: booking?.insuranceAcceptedLabel || "",
        },
      });

      const nextClientSecret = String(nextIntent?.clientSecret || "").trim();
      const nextPaymentIntentId = String(nextIntent?.paymentIntentId || "").trim();
      const nextPlatformAccountId = String(nextIntent?.platformAccountId || "").trim();
      const nextPublishableKey = String(nextIntent?.publishableKey || "").trim();
      const nextLivemode = typeof nextIntent?.livemode === "boolean" ? nextIntent.livemode : null;
      const runtimeHost =
        typeof window !== "undefined" ? String(window.location.hostname || "").trim().toLowerCase() : "";
      const isStagingHost =
        runtimeHost === "staging.afrimed.tech" ||
        runtimeHost.endsWith("--afrimed-staging.netlify.app") ||
        runtimeHost === "localhost" ||
        runtimeHost === "127.0.0.1" ||
        runtimeHost === "::1";

      setIntentState({
        loading: false,
        error: "",
        clientSecret: nextClientSecret,
        paymentIntentId: nextPaymentIntentId,
        platformAccountId: nextPlatformAccountId,
        publishableKey: nextPublishableKey,
        livemode: nextLivemode,
        applicationFeeAmount: Number(nextIntent?.application_fee_amount || 0),
        doctorNetAmount: Number(nextIntent?.doctor_net_amount || 0),
        currency: String(nextIntent?.currency || currency || "ZAR").trim() || "ZAR",
        customerReused: Boolean(nextIntent?.customerReused),
      });

      if (!nextClientSecret) {
        throw new Error(
          t(
            "appointments.checkout.errors.prepare",
            "Secure checkout could not be prepared cleanly. Refresh payment details and try again."
          )
        );
      }

      if (!nextPublishableKey) {
        throw new Error(
          t(
            "appointments.checkout.errors.publishableKeyMissing",
            "Secure card checkout is not configured correctly. Please contact AfriMed™ support."
          )
        );
      }

      if (isStagingHost && nextLivemode === true) {
        throw new Error(
          t(
            "appointments.checkout.errors.stagingKey",
            "Secure card checkout is not available in this staging environment. Refresh payment details or contact AfriMed™ support."
          )
        );
      }

      const checkoutStripe = savedPaymentMethodId
        ? await getCheckoutStripe({
            livemode: nextLivemode === true,
            publishableKey: nextPublishableKey,
          })
        : stripe;

      if (!checkoutStripe) {
        throw new Error(
          t(
            "appointments.checkout.errors.stripeNotConfigured",
            "Secure card checkout is not configured yet on this environment."
          )
        );
      }

      if (!savedPaymentMethodId) {
        const currentPublishableKey = String(intentState.publishableKey || "").trim();
        if (nextPublishableKey && currentPublishableKey !== nextPublishableKey) {
          setCheckoutRefreshKey((current) => current + 1);
          throw new Error(
            t(
              "appointments.checkout.errors.keyRefreshRequired",
              "Secure checkout keys were refreshed for this payment. Please tap Pay and book again."
            )
          );
        }
      }

      const result = await checkoutStripe.confirmCardPayment(
        nextClientSecret,
        savedPaymentMethodId
          ? {
              payment_method: savedPaymentMethodId,
            }
          : {
              payment_method: {
                card: cardElement,
                billing_details: {
                  email: guardianEmail || undefined,
                  name: guardianName || undefined,
                },
              },
            },
      );

      if (result.error) {
        const stripeErrorMessage = String(result.error.message || "").trim().toLowerCase();
        if (
          stripeErrorMessage.includes("no such payment_intent") ||
          stripeErrorMessage.includes("does not match any associated paymentintent")
        ) {
          throw new Error(buildPaymentIntentMismatchMessage(nextPlatformAccountId, t));
        }
        throw new Error(
          result.error.message ||
            t("appointments.checkout.errors.paymentFailed", "Payment could not be completed.")
        );
      }

      const paymentStatus = String(result?.paymentIntent?.status || "").trim().toLowerCase();
      if (paymentStatus && paymentStatus !== "succeeded") {
        throw new Error(
          t(
            "appointments.checkout.errors.processing",
            "Your payment is still processing. Please wait a moment and try again."
          )
        );
      }

      const paymentIntentId =
        String(result?.paymentIntent?.id || nextPaymentIntentId || intentState.paymentIntentId || "").trim();
      if (!paymentIntentId) {
        throw new Error(
          t(
            "appointments.checkout.errors.paymentReferenceMissing",
            "Payment completed but the payment reference was missing."
          )
        );
      }

      const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
      if (!startsAt) {
        throw new Error(
          t(
            "appointments.checkout.errors.invalidTime",
            "Payment succeeded, but the appointment time was invalid."
          )
        );
      }

      const { data, error } = await createAppointment({
        doctorId: bookingDoctorId,
        practiceId:
          booking?.facilityId ||
          booking?.facility?.id ||
          doctor?.facility_id ||
          doctor?.practice_id ||
          null,
        startsAt,
        note: "",
        consultationType,
        duration: bookingDurationMinutes,
        serviceId: booking?.serviceId || booking?.selectedServiceId || null,
        serviceName,
        servicePrice: amountPaid > 0 ? amountPaid : null,
        serviceCurrency: currency || null,
        careSubject,
      });

      if (error) throw error;

      const appointmentId = String(data?.id || "").trim();
      if (!appointmentId) {
        throw new Error(
          t(
            "appointments.checkout.errors.createAppointment",
            "Payment succeeded, but the appointment could not be created."
          )
        );
      }

      paymentCompletedRef.current = true;
      logPaymentEvent("appointment_created", {
        appointment_id: appointmentId,
        payment_intent_present: Boolean(paymentIntentId),
        payment_method: paymentMethodId || "",
      });

      const paymentUpdate = await updateAppointmentPayment(appointmentId, paymentIntentId, true);
      if (paymentUpdate?.error) {
        throw paymentUpdate.error;
      }

      await syncAppointmentPayment({
        appointment_id: appointmentId,
        payment_intent_id: paymentIntentId,
      });

      const doctorAuthId = String(
        await resolveDoctorAuthUserId({
          appointmentId,
          doctorUserId: data?.doctor_id || bookingDoctorId,
          doctorRef: bookingDoctorId,
        }),
      ).trim();

      if (doctorAuthId) {
        try {
          await createAppointmentNotification({
            userId: doctorAuthId,
            title: t("appointments.confirm.notifications.newAppointmentTitle", "New appointment"),
            message: t(
              "appointments.checkout.notifications.newAppointmentSecured",
              "{name} requested an appointment, on {datetime}. Payment is already secured.",
              {
                name: patientName,
                datetime: formatNotificationAppointmentDate(startsAt, languageCode),
              }
            ),
            type: `appointment_booking_request:${appointmentId}`,
            priority: "urgent",
          });
        } catch (notificationError) {
          console.warn("Unable to create doctor payment notification:", notificationError);
        }
      }

      const patientAuthId = String(auth?.user?.id || "").trim();
      if (patientAuthId) {
        try {
          await createAppointmentNotification({
            userId: patientAuthId,
            title: t(
              "appointments.checkout.notifications.patientAppointmentRequestedTitle",
              "Appointment requested",
            ),
            message: t(
              "appointments.checkout.notifications.patientAppointmentRequested",
              "Your appointment request for {datetime} was sent.",
              {
                datetime: formatNotificationAppointmentDate(startsAt, languageCode),
              },
            ),
            type: `appointment_booking_request:${appointmentId}:patient`,
            priority: "urgent",
          });
        } catch (notificationError) {
          console.warn("Unable to create patient booking notification:", notificationError);
        }
      }

      try {
        await sendAppointmentEventEmail({
          appointmentId,
          eventType: "booking_created",
        });
      } catch (emailError) {
        console.warn("Unable to send appointment booking email:", emailError);
      }

      const appointmentReminderPreference = await persistPatientAppointmentReminderDefault(
        auth?.user?.id,
      );
      const successState = {
        doctor,
        appointmentId,
        amountPaid,
        currency,
        careSubjectType: careSubject?.type || "self",
        careSubjectId: careSubject?.dependantId || careSubject?.selectionId || null,
        careSubjectName: patientName,
        careSubjectRelationship: careSubject?.relationship || "",
        careSubjectDateOfBirth: careSubject?.dateOfBirth || null,
        guardianUserId: auth?.user?.id || null,
        guardianName,
        patientName,
        patient_name: patientName,
        patient: {
          name: patientName,
          guardian_name: guardianName,
          care_subject_type: careSubject?.type || "self",
          care_subject_relationship: careSubject?.relationship || "",
          responsible_relationship: careSubject?.relationship || "",
        },
        paymentLabel,
        insurancePaymentMode: booking?.insurancePaymentMode || "self_pay",
        insuranceProvider: booking?.insuranceProvider || "",
        insuranceContextLabel,
        insuranceProviderOptions: booking?.insuranceProviderOptions || [],
        insuranceAcceptedLabel: booking?.insuranceAcceptedLabel || "",
        paymentIntentId,
        serviceId: booking?.serviceId || booking?.selectedServiceId || null,
        serviceName,
        serviceNamePrice: formatAmount(amountPaid, currency, languageCode),
        selectedDate: booking?.selectedDate,
        selectedTime: booking?.selectedTime,
        mode: booking?.mode || null,
        facilityId: booking?.facilityId || null,
        facilityName: booking?.facilityName || "",
        facility: booking?.facility || null,
        consultation_type: consultationType,
        practice_id:
          booking?.facilityId ||
          booking?.facility?.id ||
          doctor?.facility_id ||
          doctor?.practice_id ||
          null,
        when: {
          dateISO: booking?.selectedDate,
          time: booking?.selectedTime,
          duration_mins: bookingDurationMinutes,
        },
        duration: bookingDurationMinutes,
        durationMinutes: bookingDurationMinutes,
        duration_minutes: bookingDurationMinutes,
        appointmentRemindersDefaulted: appointmentReminderPreference.defaulted,
        appointmentReminderRevision: appointmentReminderPreference.updatedAt || null,
        appointmentStatus: String(data?.status || "pending").trim().toLowerCase() || "pending",
      };

      persistSnapshots(appointmentId, successState);
      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      window.dispatchEvent(new Event("afrimed:notifications:changed"));
      window.dispatchEvent(new Event("afrimed:transactions:changed"));
      nav("/appointments/success", { state: successState });
    } catch (error) {
      logPaymentEvent("checkout_failed", {
        error_category: categorizeCheckoutError(error),
        payment_method: paymentMethodId || "",
        stripe_intent_present: Boolean(intentState.paymentIntentId),
      });
      setIntentState((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || "",
      }));
      setSubmitError(formatCheckoutError(error, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWorldlineSandboxCheckout() {
    const serviceId = String(booking?.serviceId || booking?.selectedServiceId || "").trim();
    const startsAt = toAppointmentISO(booking?.selectedDate, booking?.selectedTime);
    if (!bookingDoctorId || !serviceId || !startsAt) {
      setSubmitError(t("worldline.checkout.incomplete", "Booking details are incomplete for Worldline sandbox checkout."));
      return;
    }
    setWorldlineStarting(true);
    setSubmitError("");
    try {
      if (!worldlineIdempotencyRef.current) worldlineIdempotencyRef.current = crypto.randomUUID();
      const result = await createWorldlineHostedCheckout({
        clinicianUserId: bookingDoctorId,
        serviceId,
        startsAt,
        idempotencyKey: worldlineIdempotencyRef.current,
        locale: languageCode,
      });
      const attemptId = String(result?.attempt_id || "").trim();
      const redirectUrl = String(result?.redirect_url || "").trim();
      if (!attemptId || !redirectUrl.startsWith("https://")) throw new Error("Hosted checkout was not returned.");
      sessionStorage.setItem("afrimed:worldline:attempt", attemptId);
      window.location.assign(redirectUrl);
    } catch (error) {
      setSubmitError(error?.message || t("worldline.checkout.unavailable", "Worldline sandbox checkout is unavailable."));
      setWorldlineStarting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.checkout.title", "Secure checkout")}
          titleClassName="text-headline-small text-on-surface"
          close
          onBack={handleBackToCheckout}
        />

        {!booking && !isSponsorReturn ? (
          <Card className="mt-6 p-5 text-body-large text-on-surface-var">
            {t(
              "appointments.checkout.notFound",
              "Booking details not found. Please go back and start again."
            )}
            <div className="mt-4">
              <Button fullWidth onClick={() => nav("/appointments/doctors")}>
                {t("appointments.book.backToDoctors", "Back to clinicians")}
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <div className="mt-6">
              <div>
                <aside className="hidden md:block md:py-5">
                  <CheckoutSidebarSummary
                    t={t}
                    appointmentLabel={appointmentLabel}
                    amountLabel={formatAmount(amountPaid, currency, languageCode)}
                    careSubjectType={careSubject?.type || "self"}
                    doctorDisplayName={doctorDisplayName}
                    doctorTermsUpdatedAt={doctorTermsState.doctorTermsUpdatedAt}
                    doctorTermsUrl={doctorTermsState.doctorTermsUrl}
                    methodsError={methodsState.error}
                    onPrepareSponsorPayment={authorizationOnly ? undefined : handlePrepareSponsorPayment}
                    paymentLabel={paymentLabel}
                    insuranceContextLabel={insuranceContextLabel}
                    patientName={patientDisplayName}
                    responsibleAdultName={responsibleAdultName}
                    serviceName={serviceName}
                    submitError={submitError}
                  />
                </aside>

                <section className="min-w-0 bg-[rgb(var(--clr-surface-container-low))] py-5 sm:py-8">
                  <div className="flex w-full flex-col gap-5">
                    <div className="flex flex-col gap-4">
                      <div className="hidden md:block">
                        <CheckoutStepper
                          activeStep={checkoutPhase === "details" ? 1 : 2}
                          showActive={checkoutPhase === "payment"}
                          steps={checkoutSteps}
                        />
                      </div>
                    </div>

                    <CheckoutSummaryMobile
                      t={t}
                      amountLabel={formatAmount(amountPaid, currency, languageCode)}
                      appointmentLabel={appointmentLabel}
                      careSubjectType={careSubject?.type || "self"}
                      doctorDisplayName={doctorDisplayName}
                      doctorTermsUpdatedAt={doctorTermsState.doctorTermsUpdatedAt}
                      doctorTermsUrl={doctorTermsState.doctorTermsUrl}
                      methodsError={methodsState.error}
                      open={mobileSummaryOpen}
                      onChooseDifferentMethod={handleChooseDifferentMethod}
                      onPrepareSponsorPayment={authorizationOnly ? undefined : handlePrepareSponsorPayment}
                      onToggle={() => setMobileSummaryOpen((current) => !current)}
                      paymentLabel={paymentLabel}
                      insuranceContextLabel={insuranceContextLabel}
                      patientName={patientDisplayName}
                      responsibleAdultName={responsibleAdultName}
                      serviceName={serviceName}
                      submitError={submitError}
                    />

                    <div className="md:hidden">
                      <CheckoutStepper
                        activeStep={checkoutPhase === "details" ? 1 : 2}
                        showActive={checkoutPhase === "payment"}
                        steps={checkoutSteps}
                      />
                    </div>

                    {isSponsorReturn && sponsorReturnState.loading ? (
                      <CheckoutStatusCard>
                        {t(
                          "appointments.checkout.sponsorFinalising",
                          "Finalising sponsor payment and restoring the appointment..."
                        )}
                      </CheckoutStatusCard>
                    ) : isSponsorReturn && sponsorReturnState.error ? (
                      <CheckoutStatusCard tone="critical">
                        <div>{sponsorReturnState.error}</div>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <Button type="button" variant="tonal" onClick={handleRefreshCheckout}>
                            {t(
                              "appointments.checkout.retrySponsorSync",
                              "Retry sponsor payment sync"
                            )}
                          </Button>
                          <Button type="button" variant="ghost" onClick={handleContinueWithCardPayment}>
                            {t(
                              "appointments.checkout.continueWithCardPayment",
                              "Continue with card payment"
                            )}
                          </Button>
                        </div>
                      </CheckoutStatusCard>
                    ) : evaluationAccessState.loading ? (
                      <CheckoutStatusCard>
                        {t("appointments.checkout.preparingOptions", "Preparing payment options...")}
                      </CheckoutStatusCard>
                    ) : paymentProviderMode === "unsupported" && paymentMethodId !== "mtn_momo" && !evaluationAccessState.available ? (
                      <CheckoutStatusCard tone="critical">
                        {t(
                          "appointments.checkout.errors.providerUnavailable",
                          "Secure payment is unavailable because no supported payment provider is configured.",
                        )}
                      </CheckoutStatusCard>
                    ) : !requiresCardCheckout && !evaluationAccessState.available && paymentMethodId !== "mtn_momo" ? (
                      <CheckoutStatusCard>
                        {t(
                          "appointments.checkout.methodNotSupported",
                          "This method does not collect payment inside AfriMed™ yet, so the appointment is not confirmed here. Choose secure card checkout instead, or use sponsor payment without losing the booked slot."
                        )}
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <Button type="button" variant="tonal" className="h-12" onClick={handlePrepareSponsorPayment}>
                            {t(
                              "appointments.checkout.useSponsorPayment",
                              "Use sponsor payment"
                            )}
                          </Button>
                          <Button type="button" variant="ghost" className="h-12" onClick={handleChooseDifferentMethod}>
                            {t(
                              "appointments.checkout.chooseDifferentMethod",
                              "Choose a different payment method"
                            )}
                          </Button>
                        </div>
                      </CheckoutStatusCard>
                    ) : !isPaystackProvider && !isWorldlineProvider && stripeState.loading && !evaluationAccessState.available ? (
                      <CheckoutStatusCard>
                        {t(
                          "appointments.checkout.preparing",
                          "Preparing secure card checkout..."
                        )}
                      </CheckoutStatusCard>
                    ) : !isPaystackProvider && !isWorldlineProvider && stripeState.error && !evaluationAccessState.available ? (
                      <CheckoutStatusCard tone="critical">
                        <div>{stripeState.error}</div>
                        <div className="mt-4">
                          <Button type="button" variant="tonal" onClick={handleRefreshCheckout}>
                            {t("appointments.checkout.refresh", "Refresh payment details")}
                          </Button>
                        </div>
                      </CheckoutStatusCard>
                    ) : !isPaystackProvider && !isWorldlineProvider && stagingStripeModeMismatch && !evaluationAccessState.available ? (
                      <CheckoutStatusCard tone="critical">
                        {t(
                          "appointments.checkout.errors.stagingKey",
                          "Secure card checkout is not available in this staging environment. Refresh payment details or contact AfriMed™ support."
                        )}
                        <div className="mt-4">
                          <Button type="button" variant="tonal" onClick={handleRefreshCheckout}>
                            {t("appointments.checkout.refresh", "Refresh payment details")}
                          </Button>
                        </div>
                      </CheckoutStatusCard>
                    ) : booking && bookingDoctorId ? (
                      <>
                        {paymentMethodId === "mtn_momo" ? <MtnMomoCheckoutForm amountLabel={formatAmount(amountPaid, currency, languageCode)} appointmentLabel={appointmentLabel} disabled={submitting || methodsState.loading || !methodsState.methods.some((method) => method.id === "mtn_momo")} state={mtnMomoState} error={submitError} onStart={handleStartMtnMomoPayment} onCheck={checkMtnMomoStatus} onBack={handleChooseDifferentMethod} /> : isPaystackProvider ? (
                          <PaystackCheckoutForm
                            t={t}
                            amountLabel={formatAmount(amountPaid, currency, languageCode)}
                            evaluationDueLabel={formatAmount(0, currency, languageCode)}
                            appointmentLabel={appointmentLabel}
                            insuranceContextLabel={insuranceContextLabel}
                            disabled={submitting}
                            submitting={submitting}
                            submitError={submitError}
                            onCheckoutPhaseChange={setCheckoutPhase}
                            onUseSponsorPayment={handlePrepareSponsorPayment}
                            evaluationAccessAvailable={evaluationAccessState.available}
                            onUseEvaluationAccess={handleEvaluationAccessBooking}
                            careSubjectConfirmationRequired={careSubjectConfirmationRequired}
                            careSubjectConfirmed={careSubjectConfirmed}
                            onCareSubjectConfirmedChange={setCareSubjectConfirmed}
                            careSubjectName={patientDisplayName}
                            onStartPaystackPayment={handleStartPaystackAppointmentPayment}
                            paystackPaymentStarting={paystackAppointmentState.starting}
                            paystackPaymentStatus={paystackAppointmentState.message}
                            onRunPaymentTest={handlePaymentTestHarness}
                            paymentTestRunning={paymentTestState.running}
                            paymentTestStatus={paymentTestState.message}
                            bookingPaymentMethod={paymentMethodId || "card"}
                            authorizationOnly={authorizationOnly}
                            bookingPaymentAvailable={!methodsState.loading && methodsState.methods.some((method) => method.id === paymentMethodId)}
                            scanToPayState={scanToPayState}
                            onCheckScanToPayStatus={checkScanToPayStatus}
                            onReturnFromScanToPay={() => setScanToPayState(null)}
                          />
                        ) : null}
                        {!isPaystackProvider ? (
                          <>
                        {worldlineSandboxEnabled && worldlineEligible && !evaluationAccessState.available ? (
                          <section className="rounded-[12px] border border-[rgb(var(--afrimed-outline))] bg-[rgb(var(--afrimed-surface))] p-4" aria-label={t("worldline.checkout.methodLabel", "Worldline payment method")}>
                            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                              <p className="text-title-small text-on-surface">{t("worldline.checkout.title", "Pay securely with Worldline")}</p>
                              <span className="text-label-small text-on-surface-var">{t("worldline.checkout.sandbox", "Worldline sandbox")}</span>
                            </div>
                            <p className="mt-1 text-body-small text-on-surface-var">{t("worldline.checkout.notice", "Sandbox only. A successful checkout authorizes the appointment; capture happens only after it is completed.")}</p>
                            <Button className="mt-3" variant="tonal" loading={worldlineStarting} disabled={submitting || worldlineStarting} onClick={handleWorldlineSandboxCheckout}>
                              {t("worldline.checkout.action", "Pay securely with Worldline")}
                            </Button>
                          </section>
                        ) : null}
                      {false ? <Elements
                        key={`${checkoutRefreshKey}:${intentState.livemode === true ? "live" : "test"}:${String(intentState.publishableKey || "").trim()}`}
                        stripe={evaluationAccessState.available ? null : stripePromise}
                        options={{
                          appearance: {
                            theme: "stripe",
                            labels: "floating",
                            variables: {
                              fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                              colorPrimary: "#2f55d4",
                              colorBackground: "#f6f8fc",
                              colorText: "#111827",
                              colorTextSecondary: "#52627a",
                              colorDanger: "#f8533a",
                              colorSuccess: "#19bac0",
                              borderRadius: "12px",
                              spacingUnit: "4px",
                            },
                          },
                        }}
                      >
                        <CardCheckoutForm
                          t={t}
                          checkoutPhase={checkoutPhase}
                          onCheckoutPhaseChange={setCheckoutPhase}
                          amountLabel={formatAmount(amountPaid, currency, languageCode)}
                          appointmentLabel={appointmentLabel}
                          insuranceContextLabel={insuranceContextLabel}
                          disabled={submitting}
                          loadError={stripeState.error}
                          submitError={submitError}
                          careSubjectConfirmationRequired={careSubjectConfirmationRequired}
                          careSubjectConfirmed={careSubjectConfirmed}
                          onCareSubjectConfirmedChange={setCareSubjectConfirmed}
                          careSubjectName={patientDisplayName}
                          rememberCard={rememberCard}
                          onRememberCardChange={setRememberCard}
                          savedPaymentMethods={isPaystackTest ? [] : savedCardsState.methods}
                          savedPaymentMethodsLoading={isPaystackTest ? false : savedCardsState.loading}
                          savedPaymentMethodsError={savedCardsState.error}
                          selectedSavedPaymentMethodId={selectedSavedPaymentMethodId}
                          useSavedPaymentMethod={useSavedPaymentMethod}
                          onSelectSavedPaymentMethod={(paymentMethodId) => {
                            setSelectedSavedPaymentMethodId(String(paymentMethodId || "").trim());
                            setUseSavedPaymentMethod(true);
                            setSubmitError("");
                          }}
                          onUseNewCard={() => {
                            setUseSavedPaymentMethod(false);
                            setSubmitError("");
                          }}
                          submitting={submitting}
                          onRetry={handleRefreshCheckout}
                          onUseSponsorPayment={handlePrepareSponsorPayment}
                          evaluationAccessAvailable={evaluationAccessState.available}
                          onUseEvaluationAccess={handleEvaluationAccessBooking}
                          onSubmit={handleCardSubmit}
                          showPaystackTestHelper={isPaystackTestHelper}
                          onRunPaymentTest={handlePaymentTestHarness}
                          paymentTestRunning={paymentTestState.running}
                          paymentTestStatus={paymentTestState.message}
                        />
                      </Elements> : null}
                          </>
                        ) : null}
                      </>
                    ) : (
                      <CheckoutStatusCard tone="critical">
                        {t(
                          "appointments.checkout.errors.bookingIncompleteRetry",
                          "Booking details are incomplete. Refresh payment details and try again."
                        )}
                        <div className="mt-4">
                          <Button type="button" variant="tonal" onClick={handleRefreshCheckout}>
                            {t("appointments.checkout.refresh", "Refresh payment details")}
                          </Button>
                        </div>
                      </CheckoutStatusCard>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </>
        )}
      </div>

      <WorldlineSponsorLinkDialog
        open={worldlineSponsorDialogOpen}
        onClose={() => setWorldlineSponsorDialogOpen(false)}
        onCreate={handleCreateWorldlineSponsorLink}
        onRevoke={handleRevokeWorldlineSponsorLink}
        state={worldlineSponsorLink}
        t={t}
      />
    </div>
  );
}
